import type { AxiosProgressEvent } from "axios";
import axios from "axios";
import qs from "qs";
import { createApiResponse, createErrorApiResponse } from "@entrilia/shared/api/axiosHelper";
import { getClientCode } from "@entrilia/shared/api/clientApiContext";
import type {
  CreateMultipleObjectMetricExtensionsRequest,
  ObjectMetricExtension,
  SaveSubmissionLayoutBlockInputRequest,
  SearchObjectMetricExtensionsRequest,
} from "@entrilia/shared/api/dataCollectionTypes";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import type { FundraisingStatus } from "@entrilia/shared/api/fundraisingTypes";
import { getDefaultHeaders } from "@entrilia/shared/api/headers.ts";
import type { PortalWidgetSettings } from "@entrilia/shared/api/portalWidgetsTypes.ts";
import type {
  ApiResponse,
  BooleanOperationResult,
  JobProgress,
  JobStartResult,
  ListWithTotal,
  PaginatedList,
  RangeRequestParams,
  SearchResponse,
} from "@entrilia/shared/api/types";
import type { WorkflowSignalType, WorkflowType } from "@entrilia/shared/api/workflowTypes";
import type { ConsentAgreement } from "@entrilia/shared/components/consentAgreement/types";
import { convertToFormData } from "@entrilia/shared/utilities/formDataHelper";
import { stringToBase64 } from "@entrilia/shared/utilities/stringHelper";
import { getBackendBaseUrl } from "@entrilia/shared/variables";
import type {
  Category,
  CreateAccessCategoryRequest,
  KnownCategoryTypes,
  UpdateAccessCategoryRequest,
} from "./types/accessTypes";
import type {
  ActivityLogExportOption,
  ActivityLogExportRequest,
  ActivityLogRecord,
  SearchActivityLogsRequest,
} from "./types/activityLogTypes.ts";
import type {
  Approval,
  ApprovalActionRequest,
  BulkApprovalActionRequest,
  ReassignApprovalRequest,
} from "./types/approvalTypes";
import type {
  AssistantActionRequest,
  AssistantAttachment,
  AssistantAttachmentDownload,
  AssistantChat,
  AssistantChatHistory,
  AssistantRunEvents,
  AssistantRunResponse,
  AssistantTask,
  AssistantTaskDetails,
  AssistantTaskRunResponse,
  AssistantTasksOverview,
  CreateThreadResponse,
  DismissTaskProblemRequest,
  RenameAssistantTaskRequest,
  RenameChatRequest,
  SendMessageRequest,
  StartAssistantTaskChatRequest,
  StartAssistantTaskChatResponse,
} from "./types/assistantTypes";
import type { BcRole } from "./types/bcTypes.ts";
import type {
  ClientDictionaries,
  ClientSettings,
  CompanyInfo,
  FundAccountingAppsStatus,
  MenuBadges,
  UpdateClientSettingsRequest,
} from "./types/clientTypes";
import type {
  AddDashboardResult,
  CopyDashboardRequest,
  CopyDashboardResult,
  Dashboard,
  DuplicateDashboardRequest,
  DuplicateDashboardResult,
  GetAllDashboardsResult,
  GetDashboardResult,
} from "./types/dashboardsTypes.ts";
import type {
  AddRequestSubmissionsRequest,
  CreateDataCollectionRequestRequest,
  CreateDataCollectionRequestTemplateVersionRequest,
  CreateDataCollectionRequestTemplateVersionResponse,
  DataCollectionRequestDetails,
  DataCollectionRequestInfo,
  DataCollectionRequestTemplateVersion,
  DataCollectionSubmissionDetails,
  DataCollectionSubmissionInfo,
  DataSubmissionConfiguration,
  ImportSubmissionBlockInputResponse,
  PublishRequestRequest,
  RenameMetricExtensionRequest,
  RequestSubmissionOperationRequest,
  SearchDataCollectionRequestRequest,
  SearchSubmissionsRequest,
  UpdateDataCollectionRequestRequest,
  UpdateMultipleMetricExtensionsRequest,
  UpdateRequestSubmissionsRequest,
} from "./types/dataCollectionTypes";
import type {
  AddChangeRequest,
  CreateDataImportRequest,
  DataExportRequest,
  DataExportResponse,
  DataImportApplication,
  DataImportApplicationOperationRequest,
  DataImportChangeRequest,
  DataImportChangesTotals,
  DataImportFileContainer,
  DataImportFileContainerCreateRequest,
  DataImportFileContentValidationRequest,
  DataImportFileContentValidationResult,
  DataImportInfo,
  DataImportMetadata,
  DataImportOption,
  DataImportRollback,
  DataImportTarget,
  DataImportValidation,
  GetDataImportChangesRequest,
  SaveDataImportToCsvRequest,
  SearchImportsQuery,
  UpdateChangeRequest,
} from "./types/dataImportTypes";
import type {
  AssignClientToConfigurationRequest,
  CreateDataModelConfigurationRequest,
  DataModelConfiguration,
  UpdateDataModelConfigurationRequest,
} from "./types/dataModelConfigurationTypes";
import type { DemoRefreshOperation, DemoRefreshTemplate } from "./types/demoRefreshTypes";
import type {
  AttachmentAggregatedDocumentViews,
  DocumentPresentationInfo,
  DocumentViews,
  DocumentViewsExport,
  FundraisingAggregatedDocumentViews,
  SearchDocumentViewsRequest,
} from "./types/documentActivityTypes";
import type {
  ChangeDocumentCollectionsStatusRequest,
  ChangeDocumentCollectionsStatusResponse,
  ChangeDocumentsAccessRequest,
  ChangeDocumentsAccessResponse,
  ChangeDocumentsStatusRequest,
  DocumentCollectionDetails,
  DocumentCollectionEmailsResponse,
  DocumentCollectionsRequest,
  DocumentCollectionsResponse,
  DocumentIdentifier,
} from "./types/documentCollectionTypes";
import type { ExportEmailsRequest, SearchSentEmailsRequest, SentEmail } from "./types/emailsTypes";
import type {
  ExecuteInvoiceActionRequest,
  ExecuteInvoiceActionResponse,
  ExpenseManagementInboxAddressesResponse,
  GetInvoiceActionChecksRequest,
  GetInvoiceActionChecksResponse,
  GetInvoiceAgentResponse,
  GetInvoiceApproversAssignmentResponse,
  GetInvoiceDocumentsDownloadTokenResponse,
  GetInvoiceSourceResponse,
  PreviewInvoiceCardResponse,
  PreviewInvoiceCardRequest,
  InvoiceDocumentsDownload,
} from "./types/expenseManagementTypes.ts";
import type {
  CreateFileTagRequest,
  DataCatalogueEntityType,
  DownloadTokenResponse,
  FileDetails,
  FileDownloadToken,
  FileInfo,
  FilesViewsResponse,
  FileTag,
  FileTagSettingsResponse,
  SearchFilesRequest,
  UpdateFileTagRequest,
} from "./types/fileTypes";
import type {
  CreateFundraisingCategoryRequest,
  CreateFundraisingRequest,
  Fundraising,
  FundraisingAccessItem,
  FundraisingDocumentIdentifier,
  FundraisingInfo,
  GetTestEmailContactsForFundraisingResponse,
  SendTestFundraisingEmailRequest,
  UpdateFundraisingAccessRequest,
  UpdateFundraisingAccessResponse,
  UpdateFundraisingCategoryRequest,
  UpdateFundraisingRequest,
  UploadFundraisingFileRequest,
  UploadFundraisingFileResponse,
} from "./types/fundraisingTypes";
import type {
  CashFlowLine,
  ChangeContactEmailAndFundPortalLoginRequest,
  ChangeContactEmailAndFundPortalLoginResponse,
  ContactDetails,
  ContactEmailAvailability,
  ContactPortalAccess,
  ContactWithFieldValues,
  CreateContactRequest,
  CreateFundInvestorRequest,
  CreateInvestorRequest,
  CreateInvestorResponse,
  DeleteEntityResponse,
  EmailRegistrationEligibilityResponse,
  FundDetails,
  FundPermissionsResponse,
  FundraisingDocument,
  FundStructureDocument,
  FundWithFieldValues,
  InvestmentLine,
  Investor,
  InvestorCashFlowsRequest,
  InvestorDocumentsDownload,
  InvestorDocumentsExport,
  InvestorInvestmentsRequest,
  InvestorPermissions,
  InvestorWithFieldValues,
  ResendContactInvitationRequest,
  ResetUserPasswordResponse,
  SearchContactsRequest,
  SearchFundsRequest,
  SearchInvestorDocumentsRequest,
  SearchInvestorsRequest,
  UpdateFundInvestorRequest,
  UpdateInvestorCommunicationMatrixRequest,
} from "./types/fundStructureTypes.ts";
import type {
  AgentInfo,
  CreateInboxRequest,
  Inbox,
  SearchInboxesRequest,
  UpdateInboxRequest,
} from "./types/inboxesTypes.ts";
import type {
  AddApiGatewaySubscriptionRequest,
  ApiGatewayEligibleUser,
  ApiGatewaySubscription,
  ApiGatewaySubscriptionsResponse,
  ApiGatewaySyncLogs,
  BackupConfigurationOptions,
  BackupMasterApplicationLogs,
  BackupMasterConfiguration,
  BackupMasterConnectionState,
  BackupMasterTestConnectionResponse,
  DwhSharesResponse,
  DwhSyncInfo,
  DwhSyncLogs,
  McpApplicationState,
  McpPiiProfile,
  PatchDwhShareRequest,
  PostDwhShareRequest,
  RunBackupOperationResponse,
  SetMcpPiiProfileRequest,
  StartDwhFullSyncResponse,
  UpdateApiGatewaySubscriptionRequest,
} from "./types/integrationsTypes.ts";
import type {
  ChangeInvestorCommunicationStatusRequest,
  InvestorCommunication,
  InvestorCommunicationBodyImage,
  InvestorCommunicationEngagement,
  InvestorCommunicationInfo,
  InvestorCommunicationRecipient,
  InvestorCommunicationType,
  InvestorCommunicationVideoUploadInfo,
  SearchInvestorCommunicationsRequest,
  UpdateInvestorCommunicationRequest,
} from "./types/investorCommunicationsTypes";
import type {
  FundPortalCoreBrandingSettings,
  FundPortalCoreBrandingSettingsUpdate,
  InvestorPortalSettingsResponse,
  InvestorPortalSettingsUpdate,
  InvestorPortalSyncStatus,
  InvestorPortalWatermarkSettings,
  SendTestInvitationEmailRequest,
  SignOnSettings,
  TestInvitationContactsResponse,
  UpdatePortalWidgetRequest,
  UpdateSignOnSettingsRequest,
} from "./types/investorPortalTypes";
import type { LogsQueryParams } from "./types/logTypes";
import type { CreateNoteRequest, Note, NotesEntityType, UpdateNoteRequest } from "./types/notesTypes";
import type {
  AddChecklistRequest,
  AddFieldToObjectClassDefinitionResponse,
  ChangeChecklistTaskStatusesRequest,
  ChecklistTask,
  CreateFieldRequest,
  CreateObjectAccessCategoryRequest,
  CreateObjectContactRequest,
  CreateObjectRequest,
  CreateOrganizationPageLayoutRequest,
  ExportObjectsToExcelRequest,
  FieldValuesResponse,
  GetRelatedObjectsRequest,
  GetRelatedObjectsResponse,
  LookupObjectReference,
  ObjectAccessCategory,
  ObjectChecklist,
  ObjectClassDefinition,
  ObjectClassDefinitionInfo,
  ObjectClassDefinitionInfoWithPermissions,
  ObjectClassDefinitionWithPermissions,
  ObjectContact,
  ObjectContactInfo,
  ObjectCounters,
  ObjectDuplicatesResponse,
  ObjectFieldValues,
  ObjectFieldValuesDetails,
  ObjectInternalUser,
  ObjectInternalUserPermissions,
  ObjectTypeToLookupReferences,
  PageLayout,
  SearchObjectDefinitionsRequest,
  SearchObjectsRequest,
  UpdateChecklistRequest,
  UpdateChecklistTaskDetailsRequest,
  UpdateEntityFieldsRequest,
  UpdateFieldRequest,
  UpdateObjectAccessCategoryRequest,
  UpdateObjectContactsRequest,
  UpdateObjectDefinitionRequest,
  UpdateObjectInternalUserPermissionsRequest,
  UpdateObjectRequest,
  ValidateUniqueFieldRequest,
} from "./types/objectTypes";
import type {
  CreatePassthroughImportRequest,
  InternalEntitiesResponse,
  PassthroughApplicationLogs,
  PassthroughCheckConnectionResponse,
  PassthroughConfiguration,
  PassthroughConnectionState,
  PassthroughFundClosingResponse,
  PassthroughFundsResponse,
} from "./types/passthroughTypes";
import type { CreateMetricRequest, Metric, UpdateMetricRequest } from "./types/portfolioMonitoringTypes";
import type { CancellationToken } from "./types/svTypes.ts";
import type {
  CreateUserConsentRequest,
  UpdateUserConsentRequest,
  UserConsentDetails,
  UserConsentInfo,
} from "./types/userConsentTypes";
import type {
  ClientUserGroupDetails,
  ClientUserGroupInfo,
  ClientUserGroupMember,
  ClientUserType,
  CompaniesEntitiesResponse,
  CompaniesResponse,
  CreateClientUserGroupRequest,
  CreateInvitationRequest,
  DeleteUserGroupResponse,
  OrganizationContact,
  OrganizationDeactivatedMembersData,
  OrganizationMember,
  OrganizationMembersData,
  OrganizationMembersInvitationData,
  UpdateClientUserGroupRequest,
  UpdateOrganizationContactsRequest,
  UpdateOrganizationUserAccessRequest,
} from "./types/userManagementTypes";
import type { LoadIdentityParams, UserInfo, UserPermissions, UserPreferences } from "./types/userTypes";
import type {
  AddVideoRequest,
  CompleteVideoUploadRequest,
  GetVideoUploadUrlRequest,
  GetVideoUploadUrlResponse,
  UpdateVideoRequest,
  VideoLibraryCategory,
  VideoLibraryItem,
} from "./types/videoLibraryTypes";
import type {
  ApprovalConfigurationVersion,
  ApproveInvestorChangeRequestRequest,
  CancelWorkflowRequest,
  CreateApprovalConfigurationVersionRequest,
  CreateWorkflowActivityChangeSetRequest,
  CurrentWorkflowApprovalConfigurationResponse,
  DocumentHtmlWithHighlightsRequest,
  DocumentHtmlWithHighlightsResponse,
  ExecuteWorkflowUserActionRequest,
  ExecuteWorkflowUserActionResponse,
  GetDocumentWorkflowBySourceResponse,
  InvestorChangeRequest,
  InvestorChangeRequestDetails,
  LaunchWorkflowRequest,
  MatchDocumentsRequest,
  MatchDocumentsResponse,
  NextWorkflowApproval,
  PreconditionFailure,
  RejectInvestorChangeRequestRequest,
  RestartWorkflowRequest,
  RetryInvestorChangeRequestRequest,
  RunDocumentWorkflowsRequest,
  RunDocumentWorkflowsResponse,
  SearchInvestorChangeRequestsRequest,
  SearchWorkflowsRequest,
  SubmitWorkflowSupervisorResponseRequest,
  SubmitWorkflowSupervisorResponseResult,
  UpdateWorkflowActivityChangeSetsRequest,
  UpdateWorkflowDefinitionStatusRequest,
  UpdateWorkflowTemplateActivityRequest,
  UpdateWorkflowTemplateInstructionsRequest,
  UploadWorkflowAttachmentResult,
  Workflow,
  WorkflowActivity,
  WorkflowChecklistSummary,
  WorkflowDefinition,
  WorkflowDetails,
  WorkflowEmailDetails,
  WorkflowGroup,
  WorkflowInfo,
  WorkflowTemplateActivity,
  WorkflowTemplateVersionDetailsResponse,
  WorkflowTemplateVersionInfo,
} from "./types/workflowTypes";

const baseUri = getBackendBaseUrl("admin");

const getIdentity = async (params: LoadIdentityParams = { loadLargeAvatarImage: false }) => {
  const { data } = await axios.get<ApiResponse<UserInfo>>(`${baseUri}/Identity`, { params });
  return data;
};

const updateUserConsentAgreements = async (consentAgreements: ConsentAgreement[]) => {
  const { data } = await axios.patch<ApiResponse<ConsentAgreement[]>>(`${baseUri}/Identity/consent-agreements`, {
    consentAgreements,
  });
  return data;
};

const updateUserPreferences = async (userPreferences: UserPreferences) => {
  const { data } = await axios.patch<ApiResponse<UserPreferences>>(`${baseUri}/Identity/preferences`, userPreferences);
  return data;
};

const getUserPermissions = async () => {
  const { data } = await axios.get<ApiResponse<UserPermissions>>(`${baseUri}/Users/current/permissions`);
  return data;
};

const getFundAccountingAppsStatus = async () => {
  const { data } = await axios.get<ApiResponse<FundAccountingAppsStatus>>(`${baseUri}/FundAccountingApps/status`, {
    headers: getDefaultHeaders(),
    timeout: 10_000,
  });
  return data;
};

const resendInvitationEmailByMessage = async (request: ResendContactInvitationRequest) => {
  const { data } = await axios.post<ApiResponse<null>>(`${baseUri}/Contacts/resend-invitation-by-message`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const resetContactPassword = async (contactId: string) => {
  const { data } = await axios.post<ApiResponse<ResetUserPasswordResponse>>(
    `${baseUri}/Contacts/${contactId}/password-reset`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const resumeContactEmailDelivery = async (contactId: string) => {
  const { data } = await axios.post<ApiResponse<void>>(`${baseUri}/Contacts/${contactId}/resume-email-delivery`, null, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const changeContactEmailAndFundPortalLogin = async (
  contactId: string,
  payload: ChangeContactEmailAndFundPortalLoginRequest
) => {
  const { data } = await axios.post<ApiResponse<ChangeContactEmailAndFundPortalLoginResponse>>(
    `${baseUri}/Contacts/${contactId}/change-email-and-fund-portal-login`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const sendInvitations = async (request: CreateInvitationRequest) => {
  const { data } = await axios.post<ApiResponse<null>>(`${baseUri}/Invitations`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getSignOnSettings = async () => {
  const { data } = await axios.get<ApiResponse<SignOnSettings>>(`${baseUri}/SignOnSettings/`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateSignOnSettings = async (payload: UpdateSignOnSettingsRequest) => {
  const formData = convertToFormData(payload);
  const { data } = await axios.patch<ApiResponse<SignOnSettings>>(`${baseUri}/SignOnSettings`, formData, {
    headers: getDefaultHeaders({ formData: true }),
  });
  return data;
};

const getClientDictionaries = async () => {
  const { data } = await axios.get<ApiResponse<ClientDictionaries>>(`${baseUri}/dictionaries`, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const getMenuBadges = async () => {
  const { data } = await axios.get<ApiResponse<MenuBadges>>(`${baseUri}/MenuBadges`, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const getClientSettings = async () => {
  const { data } = await axios.get<ApiResponse<ClientSettings>>(`${baseUri}/clients/settings`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateCompanySettings = async (payload: UpdateClientSettingsRequest) => {
  const formData = convertToFormData(payload);
  const { data } = await axios.patch<ApiResponse<CompanyInfo>>(`${baseUri}/clients/settings`, formData, {
    headers: getDefaultHeaders({ formData: true }),
  });
  return data;
};

const startDemoRefresh = async (clientCode: string) => {
  const { data } = await axios.post<ApiResponse<DemoRefreshOperation>>(`${baseUri}/DemoRefresh/${clientCode}`, null, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getLatestDemoRefresh = async (clientCode: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<DemoRefreshOperation>>(`${baseUri}/DemoRefresh/${clientCode}/latest`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const getDemoRefreshTemplate = async (clientCode: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<DemoRefreshTemplate>>(`${baseUri}/DemoRefresh/${clientCode}/template`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

// Document Collections

const getDocumentCollections = async (params: DocumentCollectionsRequest) => {
  const { data } = await axios.get<ApiResponse<DocumentCollectionsResponse>>(`${baseUri}/DocumentCollections`, {
    headers: getDefaultHeaders(),
    params,
  });
  return data;
};

const getDocumentCollectionDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<DocumentCollectionDetails>>(`${baseUri}/DocumentCollections/${id}`, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const getDocumentCollectionEmails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<DocumentCollectionEmailsResponse>>(
    `${baseUri}/DocumentCollections/${id}/email-tracking`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getDocCollectionFileDownloadInfo = async (postMessageRequestId: string, attachmentId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DocumentCollections/messages/${postMessageRequestId}/attachments/${attachmentId}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const createArchiveDownloadUrl = (clientCode: string, token: string) =>
  `${baseUri}/DocumentCollections/documents?&client=${clientCode}&token=${token}`;

const getDownloadUrlForDocumentCollection = async (id: string, clientCode: string) => {
  const { data } = await axios.post<ApiResponse<DownloadTokenResponse>>(
    `${baseUri}/DocumentCollections/${id}/download`,
    null,
    {
      headers: getDefaultHeaders(),
    }
  );

  return data.success
    ? createApiResponse(createArchiveDownloadUrl(clientCode, data.data.token))
    : createErrorApiResponse<"">(data.error);
};

const getDownloadUrlForSelectedDocuments = async (selectedDocuments: DocumentIdentifier[], clientCode: string) => {
  const { data } = await axios.post<ApiResponse<DownloadTokenResponse>>(
    `${baseUri}/DocumentCollections/download`,
    {
      selectedDocuments,
    },
    {
      headers: getDefaultHeaders(),
    }
  );

  return data.success
    ? createApiResponse(createArchiveDownloadUrl(clientCode, data.data.token))
    : createErrorApiResponse<"">(data.error);
};

const changeDocumentCollectionsStatus = async (payload: ChangeDocumentCollectionsStatusRequest) => {
  const { data } = await axios.post<ApiResponse<ChangeDocumentCollectionsStatusResponse>>(
    `${baseUri}/DocumentCollections/change-status`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const changeDocumentCollectionDocumentsStatus = async (payload: ChangeDocumentsStatusRequest) => {
  const { data } = await axios.post<ApiResponse<ChangeDocumentCollectionsStatusResponse>>(
    `${baseUri}/DocumentCollections/documents/change-status`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const changeDocumentCollectionDocumentsAccess = async (payload: ChangeDocumentsAccessRequest) => {
  const { data } = await axios.post<ApiResponse<ChangeDocumentsAccessResponse>>(
    `${baseUri}/DocumentCollections/documents/change-access`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const exportDocumentCollectionsToExcel = async () => {
  const { data } = await axios.post<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DocumentCollections/excel-export`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Emails

const searchInvestorSentEmails = async (payload: SearchSentEmailsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<SentEmail>>>(
    `${baseUri}/Emails/investor/search`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );

  return data;
};

const startInvestorSentEmailsExport = async (payload: ExportEmailsRequest) => {
  const { data } = await axios.post<ApiResponse<JobStartResult>>(`${baseUri}/Emails/investor/export/start`, payload, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const getInvestorSentEmailsExportProgress = async (operationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(
    `${baseUri}/Emails/investor/export/${operationId}/progress`,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const searchPortfolioSentEmails = async (payload: SearchSentEmailsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<SentEmail>>>(
    `${baseUri}/Emails/portfolio/search`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );

  return data;
};

const startPortfolioSentEmailsExport = async (payload: ExportEmailsRequest) => {
  const { data } = await axios.post<ApiResponse<JobStartResult>>(`${baseUri}/Emails/portfolio/export/start`, payload, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const getPortfolioSentEmailsExportProgress = async (operationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(
    `${baseUri}/Emails/portfolio/export/${operationId}/progress`,
    { headers: getDefaultHeaders() }
  );

  return data;
};

// User Management

const getOrganizationManagedCompanies = async () => {
  const { data } = await axios.get<ApiResponse<CompaniesResponse>>(
    `${baseUri}/UsersManagement/organization-companies`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getOrganizationCompaniesEntities = async (clientCodes: string[]) => {
  const { data } = await axios.post<ApiResponse<CompaniesEntitiesResponse>>(
    `${baseUri}/UsersManagement/organization-companies/get-entities`,
    { clientCodes },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getOrganizationMembers = async (userType?: ClientUserType) => {
  const { data } = await axios.get<ApiResponse<OrganizationMembersData>>(
    `${baseUri}/UsersManagement/organization-users`,
    {
      headers: getDefaultHeaders(),
      params: userType ? { userType } : undefined,
    }
  );
  return data;
};

const getOrganizationContacts = async () => {
  const { data } = await axios.get<ApiResponse<OrganizationContact[]>>(
    `${baseUri}/UsersManagement/organization-contacts`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateOrganizationContacts = async (request: UpdateOrganizationContactsRequest) => {
  const { data } = await axios.put<ApiResponse<OrganizationContact[]>>(
    `${baseUri}/UsersManagement/organization-contacts`,
    request,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getOrganizationDeactivatedMembers = async () => {
  const { data } = await axios.get<ApiResponse<OrganizationDeactivatedMembersData>>(
    `${baseUri}/UsersManagement/organization-deactivated-users`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const deleteOrganizationMember = async (userId: string) => {
  const { data } = await axios.delete<ApiResponse<boolean>>(`${baseUri}/UsersManagement/organization-users/${userId}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getOrganizationMembersInvitations = async () => {
  const { data } = await axios.get<ApiResponse<OrganizationMembersInvitationData>>(
    `${baseUri}/UsersManagement/organization-invited-users`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const deleteOrganizationUserInvite = async (inviteId: string) => {
  const { data } = await axios.delete<ApiResponse<boolean>>(
    `${baseUri}/UsersManagement/organization-invited-users/${inviteId}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const resendOrganizationUserInvite = async (inviteId: string) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/UsersManagement/organization-invited-users/resend-email/${inviteId}`,
    null,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateOrganizationMemberAccess = async (userId: string, access: UpdateOrganizationUserAccessRequest) => {
  const { data } = await axios.put<ApiResponse<OrganizationMember>>(
    `${baseUri}/UsersManagement/organization-users/${userId}/access`,
    access,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

// Client User Groups

const getClientUserGroups = async () => {
  const { data } = await axios.get<ApiResponse<ClientUserGroupInfo[]>>(`${baseUri}/UserGroups`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getClientUserGroupDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<ClientUserGroupDetails>>(`${baseUri}/UserGroups/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createClientUserGroup = async (payload: CreateClientUserGroupRequest) => {
  const { data } = await axios.post<ApiResponse<ClientUserGroupDetails>>(`${baseUri}/UserGroups`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateClientUserGroup = async (id: string, payload: UpdateClientUserGroupRequest) => {
  const { data } = await axios.patch<ApiResponse<ClientUserGroupDetails>>(`${baseUri}/UserGroups/${id}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteClientUserGroup = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<DeleteUserGroupResponse>>(`${baseUri}/UserGroups/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getAvailableClientUsers = async () => {
  const { data } = await axios.get<ApiResponse<ClientUserGroupMember[]>>(`${baseUri}/UserGroups/available-users`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// User Consents
const getUserConsents = async () => {
  const { data } = await axios.get<ApiResponse<UserConsentInfo[]>>(`${baseUri}/UserConsents`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getUserConsentDetails = async (consentName: string) => {
  const { data } = await axios.get<ApiResponse<UserConsentDetails>>(`${baseUri}/UserConsents/${consentName}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createUserConsent = async (payload: CreateUserConsentRequest) => {
  const { data } = await axios.post<ApiResponse<string>>(`${baseUri}/UserConsents`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateUserConsent = async (consentName: string, payload: UpdateUserConsentRequest, updateVersion: boolean) => {
  const { data } = await axios.patch<ApiResponse<UserConsentDetails>>(
    `${baseUri}/UserConsents/${consentName}`,
    payload,
    {
      headers: getDefaultHeaders(),
      params: { updateVersion },
    }
  );
  return data;
};

const deleteUserConsent = async (consentName: string) => {
  const { data } = await axios.delete<ApiResponse<boolean>>(`${baseUri}/UserConsents/${consentName}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const exportUserConsentAgreementsToExcel = async (consentName: string) => {
  const { data } = await axios.post<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/UserConsents/${consentName}/user-consent-agreements/excel-export`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const convertUserConsentContentFileToHtml = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post<string>(`${baseUri}/UserConsents/convert-content-file-to-html`, formData, {
    headers: getDefaultHeaders({ formData: true }),
  });

  return data;
};

// Contacts

const sendInvitationToContacts = async (contactIds: string[]) => {
  const { data } = await axios.post<ApiResponse<{ asyncOperationId: string }>>(
    `${baseUri}/contacts/start-send-invitation`,
    { contactIds },
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const startDeleteContacts = async (contactIds: string[]) => {
  const { data } = await axios.post<ApiResponse<{ asyncOperationId: string }>>(
    `${baseUri}/contacts/start-delete`,
    { contactIds },
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const cancelContactsJob = async (asyncOperationId: string) => {
  const { data } = await axios.post<ApiResponse<null>>(
    `${baseUri}/contacts/jobs/${asyncOperationId}/cancel`,
    undefined,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getContactsJobProgress = async (asyncOperationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(`${baseUri}/contacts/jobs/${asyncOperationId}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const exportContactsToExcel = async (payload: SearchContactsRequest, fileName?: string) => {
  const { data } = await axios.post<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/ContactsByObjectType/excel-export`,
    payload,
    {
      headers: getDefaultHeaders(),
      params: { fileName },
    }
  );
  return data;
};

const getContactDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<ContactDetails>>(`${baseUri}/contacts/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getContactPortalAccess = async (contactId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<ContactPortalAccess>>(`${baseUri}/contacts/${contactId}/portal-access`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const getContactEmailAvailability = async (contactId: string, email: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<ContactEmailAvailability>>(
    `${baseUri}/contacts/${contactId}/email-availability`,
    { headers: getDefaultHeaders(), params: { email }, signal }
  );
  return data;
};

const getEmailRegistrationEligibility = async (contactId: string) => {
  const { data } = await axios.get<ApiResponse<EmailRegistrationEligibilityResponse>>(
    `${baseUri}/contacts/${contactId}/email-registration-eligibility`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createContact = async (payload: CreateContactRequest) => {
  const { data } = await axios.post<ApiResponse<ContactDetails>>(`${baseUri}/contacts`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteContact = async (contactId: string) => {
  const { data } = await axios.delete<ApiResponse<DeleteEntityResponse>>(`${baseUri}/contacts/${contactId}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getContactDocuments = async (contactId: string) => {
  const { data } = await axios.get<ApiResponse<FundStructureDocument[]>>(`${baseUri}/contacts/${contactId}/documents`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getContactFundraisingDocuments = async (contactId: string) => {
  const { data } = await axios.get<ApiResponse<FundraisingDocument[]>>(
    `${baseUri}/contacts/${contactId}/fundraising-documents`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getContactEmails = async (contactId: string) => {
  const { data } = await axios.get<ApiResponse<SentEmail[]>>(`${baseUri}/contacts/${contactId}/emails`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getAccessCategories = async () => {
  const { data } = await axios.get<ApiResponse<Category[]>>(`${baseUri}/AccessCategories`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getAccessCategoriesOfTypes = async (types: KnownCategoryTypes[]) => {
  const { data } = await axios.get<ApiResponse<Category[]>>(`${baseUri}/AccessCategories`, {
    params: { types },
    headers: getDefaultHeaders(),
  });
  return data;
};

const createAccessCategory = async (payload: CreateAccessCategoryRequest) => {
  const { data } = await axios.post<ApiResponse<Category>>(`${baseUri}/AccessCategories`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateAccessCategory = async (categoryId: string, payload: UpdateAccessCategoryRequest) => {
  const { data } = await axios.put<ApiResponse<Category>>(`${baseUri}/AccessCategories/${categoryId}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getInvestorDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<Investor>>(`${baseUri}/investors/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createInvestor = async (payload: CreateInvestorRequest) => {
  const { data } = await axios.post<ApiResponse<CreateInvestorResponse>>(`${baseUri}/investors`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getFundDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<FundDetails>>(`${baseUri}/funds/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getFundPermissions = async (fundId: string) => {
  const { data } = await axios.get<ApiResponse<FundPermissionsResponse>>(`${baseUri}/funds/${fundId}/permissions`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const searchFundDocuments = async (fundId: string, searchRequest: SearchInvestorDocumentsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<FundStructureDocument>>>(
    `${baseUri}/funds/${fundId}/investor-documents-search`,
    searchRequest,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const exportFundDocumentsToExcel = async (
  fundId: string,
  payload: SearchInvestorDocumentsRequest,
  fileName?: string
) => {
  const { data } = await axios.post<ApiResponse<InvestorDocumentsExport>>(
    `${baseUri}/funds/${fundId}/investor-documents/excel-export`,
    payload,
    { headers: getDefaultHeaders(), params: { fileName } }
  );
  return data;
};

const getFundDocumentsDownloadToken = async (fundId: string, payload: SearchInvestorDocumentsRequest) => {
  const { data } = await axios.post<ApiResponse<InvestorDocumentsDownload>>(
    `${baseUri}/funds/${fundId}/investor-documents/download`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateFundCommunicationMatrix = async (fundId: string, payload: UpdateInvestorCommunicationMatrixRequest) => {
  const { data } = await axios.patch<ApiResponse<FundPermissionsResponse>>(
    `${baseUri}/funds/${fundId}/communication-matrix`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const createAssistantThread = async () => {
  const { data } = await axios.post<ApiResponse<CreateThreadResponse>>(`${baseUri}/Assistant/threads`, null, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const uploadAssistantAttachment = async (threadId: string, file: File, signal?: AbortSignal) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("threadId", threadId);

  const { data } = await axios.post<ApiResponse<AssistantAttachment>>(`${baseUri}/Assistant/attachments`, formData, {
    headers: getDefaultHeaders({ formData: true }),
    signal,
  });

  return data;
};

const getAssistantAttachmentDownload = async (threadId: string, attachmentId: string) => {
  const { data } = await axios.get<ApiResponse<AssistantAttachmentDownload>>(
    `${baseUri}/Assistant/attachments/${attachmentId}`,
    { headers: getDefaultHeaders(), params: { threadId } }
  );

  return data;
};

const getAssistantChat = async (threadId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<AssistantChatHistory>>(
    `${baseUri}/Assistant/threads/${encodeURIComponent(threadId)}`,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );

  return data;
};

const getAssistantChats = async (signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<AssistantChat[]>>(`${baseUri}/Assistant/threads`, {
    headers: getDefaultHeaders(),
    signal,
  });

  return data;
};

const deleteAssistantChat = async (threadId: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/Assistant/threads/${encodeURIComponent(threadId)}`,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const renameAssistantChat = async (threadId: string, request: RenameChatRequest) => {
  const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/Assistant/threads/${encodeURIComponent(threadId)}/title`,
    request,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const getAssistantTasksOverview = async (signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<AssistantTasksOverview>>(`${baseUri}/Assistant/tasks/overview`, {
    headers: getDefaultHeaders(),
    signal,
  });

  return data;
};

const getAssistantTask = async (taskId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<AssistantTaskDetails>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}`,
    { headers: getDefaultHeaders(), signal }
  );

  return data;
};

const renameAssistantTask = async (taskId: string, request: RenameAssistantTaskRequest) => {
  const { data } = await axios.put<ApiResponse<AssistantTask>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}/name`,
    request,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const dismissAssistantTaskProblem = async (taskId: string, request: DismissTaskProblemRequest) => {
  const { data } = await axios.post<ApiResponse<AssistantTask>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}/dismiss`,
    request,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const pauseAssistantTask = async (taskId: string) => {
  const { data } = await axios.post<ApiResponse<AssistantTask>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}/pause`,
    null,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const resumeAssistantTask = async (taskId: string) => {
  const { data } = await axios.post<ApiResponse<AssistantTask>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}/resume`,
    null,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const runAssistantTask = async (taskId: string) => {
  const { data } = await axios.post<ApiResponse<AssistantTaskRunResponse>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}/run`,
    null,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const startAssistantTaskChat = async (taskId: string, request: StartAssistantTaskChatRequest) => {
  const { data } = await axios.post<ApiResponse<StartAssistantTaskChatResponse>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}/chats`,
    request,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const deleteAssistantTask = async (taskId: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/Assistant/tasks/${encodeURIComponent(taskId)}`,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const startAssistantRun = async (request: SendMessageRequest) => {
  const { data } = await axios.post<ApiResponse<AssistantRunResponse>>(`${baseUri}/Assistant/runs`, request, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const getAssistantRunEvents = async (runId: string, after: number) => {
  const { data } = await axios.get<ApiResponse<AssistantRunEvents>>(`${baseUri}/Assistant/runs/${runId}/events`, {
    headers: getDefaultHeaders(),
    params: { after },
  });

  return data;
};

const stopAssistantRun = async (runId: string) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/Assistant/runs/${runId}/stop`,
    null,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const approveAssistantAction = async (request: AssistantActionRequest) => {
  const { data } = await axios.post<ApiResponse<AssistantRunResponse>>(
    `${baseUri}/Assistant/actions/approve`,
    request,
    { headers: getDefaultHeaders() }
  );

  return data;
};

const rejectAssistantAction = async (request: AssistantActionRequest) => {
  const { data } = await axios.post<ApiResponse<AssistantRunResponse>>(`${baseUri}/Assistant/actions/reject`, request, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const getAssistantSubscriptionToken = async () => {
  const { data } = await axios.get<ApiResponse<string>>(`${baseUri}/Assistant/subscription-token`, {
    headers: getDefaultHeaders(),
  });

  return data;
};

const searchInvestorDocuments = async (investorId: string, searchRequest: SearchInvestorDocumentsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<FundStructureDocument>>>(
    `${baseUri}/investors/${investorId}/investor-documents-search`,
    searchRequest,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const exportInvestorDocumentsToExcel = async (
  investorId: string,
  payload: SearchInvestorDocumentsRequest,
  fileName?: string
) => {
  const { data } = await axios.post<ApiResponse<InvestorDocumentsExport>>(
    `${baseUri}/investors/${investorId}/investor-documents/excel-export`,
    payload,
    { headers: getDefaultHeaders(), params: { fileName } }
  );
  return data;
};

const getInvestorDocumentsDownloadToken = async (investorId: string, payload: SearchInvestorDocumentsRequest) => {
  const { data } = await axios.post<ApiResponse<InvestorDocumentsDownload>>(
    `${baseUri}/investors/${investorId}/investor-documents/download`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvestorFundraisingDocuments = async (investorId: string) => {
  const { data } = await axios.get<ApiResponse<FundraisingDocument[]>>(
    `${baseUri}/investors/${investorId}/fundraising-documents`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvestorPermissions = async (investorId: string) => {
  const { data } = await axios.get<ApiResponse<InvestorPermissions>>(`${baseUri}/investors/${investorId}/permissions`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getInvestorInvestments = async (
  investorId: string,
  request: InvestorInvestmentsRequest,
  signal?: AbortSignal
): Promise<ApiResponse<InvestmentLine[]>> => {
  const { data } = await axios.post<ApiResponse<InvestmentLine[]>>(
    `${baseUri}/investors/${investorId}/investments`,
    request,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const getInvestorCashFlows = async (investorId: string, request: InvestorCashFlowsRequest) => {
  const { data } = await axios.post<ApiResponse<CashFlowLine[]>>(
    `${baseUri}/investors/${investorId}/cash-flows`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateInvestorCommunicationMatrix = async (payload: UpdateInvestorCommunicationMatrixRequest) => {
  const { data } = await axios.patch<ApiResponse<void>>(`${baseUri}/investors/communication-matrix`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createFundInvestor = async (payload: CreateFundInvestorRequest) => {
  const { data } = await axios.post<ApiResponse<void>>(`${baseUri}/FundInvestors`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateFundInvestor = async (id: string, payload: UpdateFundInvestorRequest) => {
  const { data } = await axios.patch<ApiResponse<void>>(`${baseUri}/FundInvestors/${id}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Specific Object Entities

const searchFunds = async (request: SearchFundsRequest) => {
  const { data } = await axios.post<ApiResponse<SearchResponse<FundWithFieldValues>>>(
    `${baseUri}/FundsByObjectType/search`,
    request,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getFundFieldValues = async (fundId: string) => {
  const { data } = await axios.get<ApiResponse<FieldValuesResponse>>(`${baseUri}/FundsByObjectType/${fundId}/fields`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateFundFieldValues = async (fundId: string, payload: UpdateEntityFieldsRequest) => {
  const { data } = await axios.patch<ApiResponse<FieldValuesResponse>>(
    `${baseUri}/FundsByObjectType/${fundId}/fields`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const searchInvestors = async (request: SearchInvestorsRequest) => {
  const { data } = await axios.post<ApiResponse<SearchResponse<InvestorWithFieldValues>>>(
    `${baseUri}/InvestorsByObjectType/search`,
    request,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getInvestorFieldValues = async (investorId: string) => {
  const { data } = await axios.get<ApiResponse<FieldValuesResponse>>(
    `${baseUri}/InvestorsByObjectType/${investorId}/fields`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateInvestorFieldValues = async (investorId: string, payload: UpdateEntityFieldsRequest) => {
  const { data } = await axios.patch<ApiResponse<FieldValuesResponse>>(
    `${baseUri}/InvestorsByObjectType/${investorId}/fields`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const searchContacts = async (request: SearchContactsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<ContactWithFieldValues>>>(
    `${baseUri}/ContactsByObjectType/search`,
    request,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getContactFieldValues = async (contactId: string) => {
  const { data } = await axios.get<ApiResponse<FieldValuesResponse>>(
    `${baseUri}/ContactsByObjectType/${contactId}/fields`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateContactFieldValues = async (contactId: string, payload: UpdateEntityFieldsRequest) => {
  const { data } = await axios.patch<ApiResponse<FieldValuesResponse>>(
    `${baseUri}/ContactsByObjectType/${contactId}/fields`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

// Objects

const getObjectDefinition = async (objectType: string) => {
  const { data } = await axios.get<ApiResponse<ObjectClassDefinitionWithPermissions>>(
    `${baseUri}/Objects/${objectType}/definition`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getAvailableObjectDefinitions = async () => {
  const { data } = await axios.get<ApiResponse<ObjectClassDefinitionInfoWithPermissions[]>>(
    `${baseUri}/Objects/definitions`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const searchObjects = async (objectType: string, payload: SearchObjectsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<ObjectFieldValues>>>(
    `${baseUri}/Objects/${objectType}/search`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const startExportObjectsToExcel = async (objectType: string, payload: ExportObjectsToExcelRequest) => {
  const { data } = await axios.post<ApiResponse<JobStartResult>>(
    `${baseUri}/Objects/${objectType}/excel-export`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectsExcelExportJobProgress = async (operationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(
    `${baseUri}/Objects/excel-export-progress/${operationId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectById = async (objectType: string, id: string) => {
  const { data } = await axios.get<ApiResponse<ObjectFieldValuesDetails>>(`${baseUri}/Objects/${objectType}/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getObjectEtag = async (objectType: string, objectId: string) => {
  const { data } = await axios.get<ApiResponse<string>>(`${baseUri}/Objects/${objectType}/${objectId}/etag`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getObjectDuplicates = async (objectType: string, objectId: string) => {
  const { data } = await axios.get<ApiResponse<ObjectDuplicatesResponse>>(
    `${baseUri}/Objects/${objectType}/${objectId}/duplicates`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createObject = async (objectType: string, payload: CreateObjectRequest) => {
  const { data } = await axios.post<ApiResponse<ObjectFieldValues>>(`${baseUri}/Objects/${objectType}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateObject = async (objectType: string, id: string, payload: UpdateObjectRequest) => {
  const { data } = await axios.patch<ApiResponse<ObjectFieldValuesDetails>>(
    `${baseUri}/Objects/${objectType}/${id}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteObject = async (objectType: string, id: string) => {
  const { data } = await axios.delete<ApiResponse<{ objectId: string }>>(`${baseUri}/Objects/${objectType}/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateObjectLogo = async (objectType: string, id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.put<ApiResponse<ObjectFieldValuesDetails>>(
    `${baseUri}/Objects/${objectType}/${id}/logo`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );

  return data;
};

const removeObjectLogo = async (objectType: string, id: string) => {
  const { data } = await axios.delete<ApiResponse<ObjectFieldValuesDetails>>(
    `${baseUri}/Objects/${objectType}/${id}/logo`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const validateUniqueField = async (objectType: string, payload: ValidateUniqueFieldRequest) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/Objects/${objectType}/validate-unique-field`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getLookupOptions = async (objectType: string, objectId?: string) => {
  const { data } = await axios.get<ApiResponse<ObjectTypeToLookupReferences>>(
    `${baseUri}/Objects/${objectType}/lookup-options`,
    {
      headers: getDefaultHeaders(),
      params: { objectId },
    }
  );
  return data;
};

const getRelatedObjects = async (objectType: string, objectId: string, payload: GetRelatedObjectsRequest) => {
  const { data } = await axios.post<ApiResponse<GetRelatedObjectsResponse>>(
    `${baseUri}/Objects/${objectType}/${objectId}/get-related-objects`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectsAsLookupReferences = async (objectType: string) => {
  const { data } = await axios.get<ApiResponse<LookupObjectReference[]>>(
    `${baseUri}/Objects/${objectType}/as-lookup-references`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const countObjectsByType = async (objectType: string) => {
  const { data } = await axios.get<ApiResponse<ObjectCounters>>(`${baseUri}/Objects/${objectType}/counters`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const changeChecklistTaskStatuses = async (
  objectType: string,
  objectId: string,
  payload: ChangeChecklistTaskStatusesRequest
) => {
  const { data } = await axios.post<ApiResponse<ChecklistTask[]>>(
    `${baseUri}/Objects/${objectType}/${objectId}/checklist-tasks/change-status`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateChecklistTaskDetails = async (
  objectType: string,
  objectId: string,
  payload: UpdateChecklistTaskDetailsRequest
) => {
  const { data } = await axios.post<ApiResponse<ChecklistTask>>(
    `${baseUri}/Objects/${objectType}/${objectId}/checklist-tasks/update-details`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectInternalUserPermissions = async (objectType: string, objectId: string) => {
  const { data } = await axios.get<ApiResponse<ObjectInternalUserPermissions[]>>(
    `${baseUri}/ObjectInternalUsers/${objectType}/${objectId}/internal-user-permissions`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateObjectInternalUserPermissions = async (
  objectType: string,
  objectId: string,
  payload: UpdateObjectInternalUserPermissionsRequest
) => {
  const { data } = await axios.put<ApiResponse<ObjectInternalUserPermissions[]>>(
    `${baseUri}/ObjectInternalUsers/${objectType}/${objectId}/internal-user-permissions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectInternalUsers = async (objectType: string) => {
  const { data } = await axios.get<ApiResponse<ObjectInternalUser[]>>(
    `${baseUri}/ObjectInternalUsers/${objectType}/internal-users`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getAllContactsForObjectType = async (objectType: string) => {
  const { data } = await axios.get<ApiResponse<ObjectContactInfo[]>>(
    `${baseUri}/ObjectContacts/${objectType}/contacts`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createObjectContact = async (objectType: string, payload: CreateObjectContactRequest) => {
  const { data } = await axios.post<ApiResponse<ObjectContactInfo>>(
    `${baseUri}/ObjectContacts/${objectType}/contacts`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectContacts = async (objectType: string, objectId: string) => {
  const { data } = await axios.get<ApiResponse<ObjectContact[]>>(
    `${baseUri}/ObjectContacts/${objectType}/${objectId}/contacts`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateObjectContacts = async (objectType: string, objectId: string, payload: UpdateObjectContactsRequest) => {
  const { data } = await axios.put<ApiResponse<ObjectContact[]>>(
    `${baseUri}/ObjectContacts/${objectType}/${objectId}/contacts`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectAccessCategories = async () => {
  const { data } = await axios.get<ApiResponse<ObjectAccessCategory[]>>(`${baseUri}/ObjectAccessCategories`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createObjectAccessCategory = async (payload: CreateObjectAccessCategoryRequest) => {
  const { data } = await axios.post<ApiResponse<ObjectAccessCategory>>(`${baseUri}/ObjectAccessCategories`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateObjectAccessCategory = async (categoryId: string, payload: UpdateObjectAccessCategoryRequest) => {
  const { data } = await axios.patch<ApiResponse<ObjectAccessCategory>>(
    `${baseUri}/ObjectAccessCategories/${categoryId}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Object Page Layouts

const getCardLayout = async (objectType: string) => {
  const { data } = await axios.get<ApiResponse<PageLayout>>(`${baseUri}/PageLayouts/${objectType}/card`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Notes

const getNotes = async (entityType: NotesEntityType, entityId: string, objectType?: string) => {
  const { data } = await axios.get<ApiResponse<Note[]>>(`${baseUri}/notes/${entityType}/${entityId}/notes`, {
    headers: getDefaultHeaders(),
    params: { objectType },
  });
  return data;
};

const createNote = async (
  entityType: NotesEntityType,
  entityId: string,
  payload: CreateNoteRequest,
  objectType?: string
) => {
  const { data } = await axios.post<ApiResponse<Note>>(`${baseUri}/notes/${entityType}/${entityId}/notes`, payload, {
    headers: getDefaultHeaders(),
    params: { objectType },
  });
  return data;
};

const updateNote = async (
  entityType: NotesEntityType,
  entityId: string,
  noteId: string,
  payload: UpdateNoteRequest,
  objectType?: string
) => {
  const { data } = await axios.patch<ApiResponse<Note>>(
    `${baseUri}/notes/${entityType}/${entityId}/notes/${noteId}`,
    payload,
    {
      headers: getDefaultHeaders(),
      params: { objectType },
    }
  );
  return data;
};

const deleteNote = async (entityType: NotesEntityType, entityId: string, noteId: string, objectType?: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/notes/${entityType}/${entityId}/notes/${noteId}`,
    {
      headers: getDefaultHeaders(),
      params: { objectType },
    }
  );
  return data;
};

// DocumentManagement

const getFilesViews = async () => {
  const { data } = await axios.get<ApiResponse<FilesViewsResponse>>(`${baseUri}/documentManagement/views`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getFileTagSettings = async () => {
  const { data } = await axios.get<ApiResponse<FileTagSettingsResponse>>(`${baseUri}/documentManagement/tag-settings`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createFileTag = async (request: CreateFileTagRequest) => {
  const { data } = await axios.post<ApiResponse<FileTag>>(`${baseUri}/documentManagement/tag`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateFileTag = async (catalogueId: string, request: UpdateFileTagRequest) => {
  const { data } = await axios.patch<ApiResponse<FileTag>>(
    `${baseUri}/documentManagement/tag/${catalogueId}`,
    request,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const deleteFileTag = async (catalogueId: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/documentManagement/tag/${catalogueId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getCompanyFiles = async (payload: SearchFilesRequest) => {
  const { range, ...filter } = payload;
  const { data } = await axios.get<ApiResponse<ListWithTotal<FileInfo>>>(`${baseUri}/documentManagement/files`, {
    params: { ...range, ...filter },
    headers: getDefaultHeaders(),
  });
  return data;
};

const getFileDownloadInfo = async (catalogueId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/documentManagement/files/${catalogueId}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getDownloadUrlForMultipleFiles = async (catalogueIds: string[], fileName: string) => {
  const { data } = await axios.post<ApiResponse<string>>(
    `${baseUri}/documentManagement/files/token`,
    { catalogueIds },
    { headers: getDefaultHeaders() }
  );

  const token = data.data;
  const query = qs.stringify({
    fileName,
    token,
    clientCode: getClientCode(),
  });

  return token
    ? createApiResponse(`${baseUri}/documentManagement/multiple-files?${query}`)
    : createErrorApiResponse<"">(data.error);
};

const renameFile = async (catalogueId: string, fileName: string) => {
  const { data } = await axios.patch<ApiResponse<FileInfo>>(
    `${baseUri}/documentManagement/files/${catalogueId}/rename`,
    { fileName },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteFiles = async (catalogueIds: string[]) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(`${baseUri}/documentManagement/files`, {
    params: { catalogueIds },
    headers: getDefaultHeaders(),
  });
  return data;
};

const getFileDetails = async (catalogueId: string) => {
  const { data } = await axios.get<ApiResponse<FileDetails>>(
    `${baseUri}/documentManagement/files/${catalogueId}/details`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// EntityDocumentManagement

const getFilesViewsForEntityType = async (entityType: DataCatalogueEntityType, objectType: string | undefined) => {
  const { data } = await axios.get<ApiResponse<FilesViewsResponse>>(
    `${baseUri}/entityDocumentManagement/${entityType}/views`,
    { params: { objectType }, headers: getDefaultHeaders() }
  );
  return data;
};

const getFileTagsForEntityType = async (entityType: DataCatalogueEntityType, objectType: string | undefined) => {
  const { data } = await axios.get<ApiResponse<FileTag[]>>(`${baseUri}/entityDocumentManagement/${entityType}/tags`, {
    params: { objectType },
    headers: getDefaultHeaders(),
  });
  return data;
};

const getEntityFiles = async (
  entityType: DataCatalogueEntityType,
  entityId: string,
  objectType: string | undefined,
  payload: SearchFilesRequest
) => {
  const { range, ...filter } = payload;
  const { data } = await axios.get<ApiResponse<ListWithTotal<FileInfo>>>(
    `${baseUri}/entityDocumentManagement/${entityType}/${entityId}/files`,
    { params: { ...range, ...filter, objectType }, headers: getDefaultHeaders() }
  );
  return data;
};

const getEntityFileDownloadInfo = async (
  entityType: DataCatalogueEntityType,
  entityId: string,
  objectType: string | undefined,
  catalogueId: string
) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/entityDocumentManagement/${entityType}/${entityId}/files/${catalogueId}`,
    { params: { objectType }, headers: getDefaultHeaders() }
  );
  return data;
};

const getDownloadUrlForMultipleEntityFiles = async (
  entityType: DataCatalogueEntityType,
  entityId: string,
  objectType: string | undefined,
  catalogueIds: string[],
  fileName: string
) => {
  const { data } = await axios.post<ApiResponse<string>>(
    `${baseUri}/entityDocumentManagement/${entityType}/${entityId}/files/token`,
    { catalogueIds },
    { params: { objectType }, headers: getDefaultHeaders() }
  );

  const token = data.data;
  const query = qs.stringify({
    fileName,
    token,
    clientCode: getClientCode(),
  });

  return token
    ? createApiResponse(`${baseUri}/documentManagement/multiple-files?${query}`)
    : createErrorApiResponse<"">(data.error);
};

const renameEntityFile = async (
  entityType: DataCatalogueEntityType,
  entityId: string,
  objectType: string | undefined,
  catalogueId: string,
  fileName: string
) => {
  const { data } = await axios.patch<ApiResponse<FileInfo>>(
    `${baseUri}/entityDocumentManagement/${entityType}/${entityId}/files/${catalogueId}/rename`,
    { fileName },
    { params: { objectType }, headers: getDefaultHeaders() }
  );
  return data;
};

const detachEntityFiles = async (
  entityType: DataCatalogueEntityType,
  entityId: string,
  objectType: string | undefined,
  catalogueIds: string[]
) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/entityDocumentManagement/${entityType}/${entityId}/files`,
    {
      params: { objectType, catalogueIds },
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getEntityFileDetails = async (
  entityType: DataCatalogueEntityType,
  entityId: string,
  objectType: string | undefined,
  catalogueId: string
) => {
  const { data } = await axios.get<ApiResponse<FileDetails>>(
    `${baseUri}/entityDocumentManagement/${entityType}/${entityId}/files/${catalogueId}/details`,
    { params: { objectType }, headers: getDefaultHeaders() }
  );
  return data;
};

const uploadEntityFile = async (
  entityType: DataCatalogueEntityType,
  entityId: string,
  objectType: string | undefined,
  file: File,
  tagId?: string
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (tagId) {
    formData.append("tagId", tagId);
  }

  const { data } = await axios.post<ApiResponse<FileInfo>>(
    `${baseUri}/entityDocumentManagement/${entityType}/${entityId}/files`,
    formData,
    {
      params: { objectType },
      headers: getDefaultHeaders({ formData: true }),
    }
  );
  return data;
};

// DWH

const getDwhShares = async () => {
  const { data } = await axios.get<ApiResponse<DwhSharesResponse>>(`${baseUri}/dwh/shares`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const postDwhShare = async (payload: PostDwhShareRequest) => {
  const { data } = await axios.post<ApiResponse<null>>(`${baseUri}/dwh/shares`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const patchDwhShare = async (id: string, payload: PatchDwhShareRequest) => {
  const { data } = await axios.patch<ApiResponse<null>>(`${baseUri}/dwh/shares/${id}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteDwhShare = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/dwh/shares/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getDwhLogs = async (params: LogsQueryParams) => {
  const { data } = await axios.get<ApiResponse<PaginatedList<DwhSyncLogs>>>(`${baseUri}/dwh/logs`, {
    params,
    headers: getDefaultHeaders(),
  });
  return data;
};

const getDwhLastSync = async () => {
  const { data } = await axios.get<ApiResponse<DwhSyncInfo>>(`${baseUri}/dwh/last-sync`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const startDwhFullSync = async () => {
  const { data } = await axios.post<ApiResponse<StartDwhFullSyncResponse>>(`${baseUri}/dwh/start-dwh-full-sync`, null, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// API Gateway
const getApiGatewayEligibleUsers = async () => {
  const { data } = await axios.get<ApiResponse<ApiGatewayEligibleUser[]>>(`${baseUri}/ApiGateway/eligible-users`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getApiGatewaySubscriptions = async () => {
  const { data } = await axios.get<ApiResponse<ApiGatewaySubscriptionsResponse>>(
    `${baseUri}/ApiGateway/subscriptions`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const addApiGatewaySubscription = async (request: AddApiGatewaySubscriptionRequest) => {
  const { data } = await axios.post<ApiResponse<ApiGatewaySubscription>>(
    `${baseUri}/ApiGateway/subscriptions`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateApiGatewaySubscription = async (id: string, request: UpdateApiGatewaySubscriptionRequest) => {
  const { data } = await axios.patch<ApiResponse<ApiGatewaySubscription>>(
    `${baseUri}/ApiGateway/subscriptions/${id}`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteApiGatewaySubscription = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/ApiGateway/subscriptions/${id}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getApiGatewayLogs = async (params: LogsQueryParams) => {
  const { data } = await axios.get<ApiResponse<PaginatedList<ApiGatewaySyncLogs>>>(`${baseUri}/ApiGateway/logs`, {
    params,
    headers: getDefaultHeaders(),
  });
  return data;
};

// Fund Portal Core

const getFundPortalCoreBrandingSettings = async () => {
  const { data } = await axios.get<ApiResponse<FundPortalCoreBrandingSettings>>(`${baseUri}/FundPortalCore`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateFundPortalCoreBrandingSettings = async (payload: FundPortalCoreBrandingSettingsUpdate) => {
  const formData = convertToFormData(payload);
  const { data } = await axios.patch<ApiResponse<FundPortalCoreBrandingSettings>>(
    `${baseUri}/FundPortalCore`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );
  return data;
};

// Investor Portal

const getInvestorPortalSettings = async () => {
  const { data } = await axios.get<ApiResponse<InvestorPortalSettingsResponse>>(`${baseUri}/InvestorPortalSettings`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateInvestorPortalSettings = async (payload: InvestorPortalSettingsUpdate) => {
  const { data } = await axios.patch<ApiResponse<InvestorPortalSettingsResponse>>(
    `${baseUri}/InvestorPortalSettings`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const sendTestInvitationEmail = async (request: SendTestInvitationEmailRequest) => {
  const { data } = await axios.post<ApiResponse<null>>(
    `${baseUri}/InvestorPortalSettings/send-test-invitation-email`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getTestInvitationContacts = async () => {
  const { data } = await axios.get<ApiResponse<TestInvitationContactsResponse>>(
    `${baseUri}/InvestorPortalSettings/test-invitation-contacts`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getWatermarkSettings = async () => {
  const { data } = await axios.get<ApiResponse<InvestorPortalWatermarkSettings>>(
    `${baseUri}/InvestorPortalSettings/watermark-settings`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateWatermarkSettings = async (settings: InvestorPortalWatermarkSettings) => {
  const { data } = await axios.put<ApiResponse<InvestorPortalWatermarkSettings>>(
    `${baseUri}/InvestorPortalSettings/watermark-settings`,
    settings,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getInvestorPortalSyncStatus = async () => {
  const { data } = await axios.get<ApiResponse<InvestorPortalSyncStatus>>(`${baseUri}/InvestorPortalSyncStatus`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getPortalWidgetSettings = async () => {
  const { data } = await axios.get<ApiResponse<PortalWidgetSettings>>(`${baseUri}/PortalWidgets`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updatePortalWidget = async (widgetId: string, payload: UpdatePortalWidgetRequest) => {
  const { data } = await axios.patch<ApiResponse<PortalWidgetSettings>>(
    `${baseUri}/PortalWidgets/${widgetId}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Backup Master

const getDataBackupConnectionState = async () => {
  const { data } = await axios.get<ApiResponse<BackupMasterConnectionState>>(
    `${baseUri}/BackupMaster/connection-state`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getDataBackupConfiguration = async () => {
  const { data } = await axios.get<ApiResponse<BackupMasterConfiguration>>(`${baseUri}/BackupMaster/configuration`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getDataBackupConfigurationOptions = async () => {
  const { data } = await axios.get<ApiResponse<BackupConfigurationOptions>>(
    `${baseUri}/BackupMaster/configuration-options`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const makeDataBackupTestConnection = async () => {
  const { data } = await axios.post<ApiResponse<BackupMasterTestConnectionResponse>>(
    `${baseUri}/BackupMaster/test-connection`,
    {},
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const setDataBackupDailySync = async (payload: { enabled: boolean }) => {
  const { data } = await axios.put<ApiResponse<BackupMasterConnectionState>>(
    `${baseUri}/BackupMaster/connection-state`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const setDataBackupConfiguration = async (payload: BackupMasterConfiguration) => {
  const { data } = await axios.put<ApiResponse<BackupMasterConnectionState>>(
    `${baseUri}/BackupMaster/configuration`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const runDataBackupNow = async () => {
  const { data } = await axios.post<ApiResponse<RunBackupOperationResponse>>(
    `${baseUri}/BackupMaster/run-backup-operation`,
    {},
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getDataBackupLogs = async (params: LogsQueryParams) => {
  const { data } = await axios.get<ApiResponse<PaginatedList<BackupMasterApplicationLogs>>>(
    `${baseUri}/BackupMaster/logs`,
    {
      params,
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

// MCP Application

const getMcpApplicationState = async () => {
  const { data } = await axios.get<ApiResponse<McpApplicationState>>(`${baseUri}/McpApplication/state`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const setMcpApplicationState = async (payload: { enabled: boolean }) => {
  const { data } = await axios.put<ApiResponse<McpApplicationState>>(`${baseUri}/McpApplication/state`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getMcpPiiProfile = async () => {
  const { data } = await axios.get<ApiResponse<McpPiiProfile>>(`${baseUri}/McpApplication/piiProfile`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const setMcpPiiProfile = async (payload: SetMcpPiiProfileRequest) => {
  const { data } = await axios.put<ApiResponse<McpPiiProfile>>(`${baseUri}/McpApplication/piiProfile`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Data Import

const searchDataImports = async (query: SearchImportsQuery) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<DataImportInfo>>>(`${baseUri}/DataImport/search`, query, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getDataImportDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<DataImportInfo>>(`${baseUri}/DataImport/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getDataImportOptions = async () => {
  const { data } = await axios.get<ApiResponse<DataImportOption[]>>(`${baseUri}/DataImport/options`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createDataImport = async (payload: CreateDataImportRequest) => {
  const { data } = await axios.post<ApiResponse<DataImportInfo>>(`${baseUri}/DataImport`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getCsvTemplateDownloadUrl = async (
  objectType: string,
  bcEntityType: string | undefined,
  dataImportTarget: DataImportTarget,
  clientName: string
) => {
  const { data } = await axios.post<ApiResponse<string>>(
    `${baseUri}/DataExport/${objectType}/csv-template/download`,
    null,
    {
      headers: getDefaultHeaders(),
      params: { dataImportTarget, bcEntityType },
    }
  );

  const token = data.data;
  const query = qs.stringify({ clientName, dataImportTarget, token });
  return data.success
    ? createApiResponse(`${baseUri}/DataExport/${objectType}/csv-template?${query}`)
    : createErrorApiResponse<"">(data.error);
};

const getDataExportDownloadUrls = async (objectType: string, payload: DataExportRequest) => {
  const { data } = await axios.post<ApiResponse<DataExportResponse>>(
    `${baseUri}/DataExport/${objectType}/data-export`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const uploadDataImportSourceFile = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post<ApiResponse<FileInfo>>(`${baseUri}/DataImport/files`, formData, {
    headers: getDefaultHeaders({ formData: true }),
  });
  return data;
};

const validateDataImportFileContent = async (request: DataImportFileContentValidationRequest) => {
  const { data } = await axios.post<ApiResponse<DataImportFileContentValidationResult>>(
    `${baseUri}/DataImport/validate-file-content`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const startDataImportParsingAndValidation = async (importId: string) => {
  const { data } = await axios.post<ApiResponse<{ validationId: string }>>(
    `${baseUri}/DataImport/${importId}/start-parsing-and-validation`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataImportValidationStatus = async (importId: string, validationId: string) => {
  const { data } = await axios.get<ApiResponse<DataImportValidation>>(
    `${baseUri}/DataImport/${importId}/validations/${validationId}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getDataImportSourceFiles = async (importId: string) => {
  const { data } = await axios.get<ApiResponse<FileInfo[]>>(`${baseUri}/DataImport/${importId}/files`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getDataImportSourceFileDownloadInfo = async (importId: string, catalogueId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DataImport/${importId}/files/${catalogueId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataImportCsvFileDownloadInfo = async (importId: string, payload: SaveDataImportToCsvRequest) => {
  const { data } = await axios.post<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DataImport/${importId}/save-to-csv`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataImportValidationSummaryCsvFileDownloadInfo = async (importId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DataImport/${importId}/save-validation-errors-summary-to-csv`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const discardDataImport = async (importId: string) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/DataImport/${importId}/discard`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const searchDataImportChanges = async (importId: string, query: GetDataImportChangesRequest) => {
  const { data } = await axios.post<ApiResponse<PaginatedList<DataImportChangeRequest>>>(
    `${baseUri}/DataImport/${importId}/changes/search`,
    query,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getDataImportChangesTotals = async (importId: string, fileDataCatalogueId?: string) => {
  const { data } = await axios.get<ApiResponse<DataImportChangesTotals>>(
    `${baseUri}/DataImport/${importId}/changes-totals`,
    {
      params: { fileDataCatalogueId },
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getDataImportMetadata = async (importId: string) => {
  const { data } = await axios.get<ApiResponse<DataImportMetadata>>(`${baseUri}/DataImport/${importId}/metadata`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const startApplyingDataImport = async (importId: string, request: DataImportApplicationOperationRequest) => {
  const { data } = await axios.post<ApiResponse<{ applicationId: string }>>(
    `${baseUri}/DataImport/${importId}/apply`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataImportApplicationStatus = async (importId: string, applicationId: string) => {
  const { data } = await axios.get<ApiResponse<DataImportApplication>>(
    `${baseUri}/DataImport/${importId}/applications/${applicationId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const startDataImportRollback = async (importId: string) => {
  const { data } = await axios.post<ApiResponse<{ rollbackId: string }>>(
    `${baseUri}/DataImport/${importId}/rollback`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataImportRollbackStatus = async (importId: string, rollbackId: string) => {
  const { data } = await axios.get<ApiResponse<DataImportRollback>>(
    `${baseUri}/DataImport/${importId}/rollbacks/${rollbackId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const addDataImportChange = async (importId: string, request: AddChangeRequest) => {
  const { data } = await axios.post<ApiResponse<DataImportChangeRequest>>(
    `${baseUri}/DataImport/${importId}/changes`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateDataImportChange = async (importId: string, changeRequestId: string, request: UpdateChangeRequest) => {
  const { data } = await axios.patch<ApiResponse<DataImportChangeRequest>>(
    `${baseUri}/DataImport/${importId}/changes/${changeRequestId}`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const excludeDataImportChange = async (importId: string, changeRequestId: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/DataImport/${importId}/changes/${changeRequestId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const revertExcludedDataImportChange = async (importId: string, changeRequestId: string) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/DataImport/${importId}/changes/${changeRequestId}/revert-excluded`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const excludeChangesWithErrors = async (importId: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/DataImport/${importId}/changes-with-errors`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataImportFileContainers = async () => {
  const { data } = await axios.get<ApiResponse<DataImportFileContainer[]>>(`${baseUri}/DataImportFileContainers`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createDataImportFileContainer = async (payload: DataImportFileContainerCreateRequest) => {
  const { data } = await axios.post<ApiResponse<DataImportFileContainer>>(
    `${baseUri}/DataImportFileContainers`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const uploadFileIntoDataImportFileContainer = async (
  id: string,
  file: File,
  onUploadProgress?: (e: AxiosProgressEvent) => void
) => {
  const { data } = await axios.post<ApiResponse<DataImportFileContainer>>(
    `${baseUri}/DataImportFileContainers/${id}/files`,
    file,
    {
      headers: {
        ...getDefaultHeaders(),
        "Content-Type": "application/octet-stream",
        "X-File-Name": stringToBase64(file.name),
      },
      onUploadProgress,
    }
  );

  return data;
};

const getUploadZipUrlForDataImportFileContainer = async (id: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadToken>>(
    `${baseUri}/DataImportFileContainers/${id}/files/zip/upload-url`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const startUnzipForDataImportFileContainer = async (id: string, fileToken: FileDownloadToken) => {
  const { data } = await axios.post<ApiResponse<JobStartResult>>(
    `${baseUri}/DataImportFileContainers/${id}/files/zip/start-unzip`,
    fileToken,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getUnzipJobProgressForDataImportFileContainer = async (id: string, operationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(
    `${baseUri}/DataImportFileContainers/${id}/files/zip/unzip-progress/${operationId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteDataImportFileContainer = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/DataImportFileContainers/${id}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getFileContainerFileDownloadInfo = async (id: string, fileName: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DataImportFileContainers/${id}/files/${encodeURIComponent(fileName)}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Passthrough Integration

const getPassthroughConfiguration = async () => {
  const { data } = await axios.get<ApiResponse<PassthroughConfiguration>>(`${baseUri}/passthrough/configuration`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updatePassthroughConfiguration = async (payload: PassthroughConfiguration) => {
  const { data } = await axios.put<ApiResponse<PassthroughConfiguration>>(
    `${baseUri}/passthrough/configuration`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const makePassthroughTestConnection = async () => {
  const { data } = await axios.get<ApiResponse<PassthroughCheckConnectionResponse>>(
    `${baseUri}/passthrough/test-connection`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getPassthroughConnectionState = async () => {
  const { data } = await axios.get<ApiResponse<PassthroughConnectionState>>(`${baseUri}/Passthrough/connection-state`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const togglePassthroughIntegration = async (payload: { enabled: boolean }) => {
  const { data } = await axios.put<ApiResponse<PassthroughConnectionState>>(
    `${baseUri}/Passthrough/connection-state`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getPassthroughLogs = async (params: LogsQueryParams) => {
  const { data } = await axios.get<ApiResponse<PaginatedList<PassthroughApplicationLogs>>>(
    `${baseUri}/Passthrough/logs`,
    {
      params,
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getPassthroughFunds = async () => {
  const { data } = await axios.get<ApiResponse<PassthroughFundsResponse>>(
    `${baseUri}/PassthroughDataImport/passthrough-funds`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getPassthroughFundClosings = async (passthroughFundId: string) => {
  const { data } = await axios.get<ApiResponse<PassthroughFundClosingResponse>>(
    `${baseUri}/PassthroughDataImport/passthrough-fund-closings`,
    {
      params: { passthroughFundId },
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getInternalEntities = async () => {
  const { data } = await axios.get<ApiResponse<InternalEntitiesResponse>>(
    `${baseUri}/PassthroughDataImport/internal-entities`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const importPassthroughData = async (request: CreatePassthroughImportRequest) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/PassthroughDataImport/passthrough-import`,
    request,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getPassthroughConnectionStatus = async () => {
  const { data } = await axios.get<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/PassthroughDataImport/passthrough-connection-status`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

// Investor Change Requests

const searchInvestorChangeRequests = async (payload: SearchInvestorChangeRequestsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<InvestorChangeRequest>>>(
    `${baseUri}/InvestorChangeRequests/search`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getInvestorChangeRequestDetails = async (workflowId: string) => {
  const { data } = await axios.get<ApiResponse<InvestorChangeRequestDetails>>(
    `${baseUri}/InvestorChangeRequests/details`,
    {
      params: { workflowId },
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getMemberContributionAttachmentDownloadInfo = async (workflowId: string, dataCatalogId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/InvestorChangeRequests/${workflowId}/members/contribution-attachments/${dataCatalogId}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getInvestorChangeRequestAttachmentDownloadInfo = async (workflowId: string, dataCatalogId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/InvestorChangeRequests/${workflowId}/attachments/${dataCatalogId}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const approveInvestorChangeRequest = async (payload: ApproveInvestorChangeRequestRequest) => {
  const { data } = await axios.post<ApiResponse<InvestorChangeRequest>>(
    `${baseUri}/InvestorChangeRequests/approve`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const rejectInvestorChangeRequest = async (payload: RejectInvestorChangeRequestRequest) => {
  const { data } = await axios.post<ApiResponse<InvestorChangeRequest>>(
    `${baseUri}/InvestorChangeRequests/reject`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const retryInvestorChangeRequest = async (payload: RetryInvestorChangeRequestRequest) => {
  const { data } = await axios.post<ApiResponse<null>>(`${baseUri}/InvestorChangeRequests/retry`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Workflows

const getWorkflowGroups = async () => {
  const { data } = await axios.get<ApiResponse<WorkflowGroup[]>>(`${baseUri}/Workflows/workflow-groups`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getWorkflowGroup = async (templateId: string) => {
  const { data } = await axios.get<ApiResponse<WorkflowGroup>>(`${baseUri}/Workflows/workflow-groups/${templateId}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const searchWorkflows = async (payload: SearchWorkflowsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<WorkflowInfo>>>(`${baseUri}/Workflows/search`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const launchWorkflow = async (payload: LaunchWorkflowRequest) => {
  const { data } = await axios.post<ApiResponse<WorkflowDetails>>(`${baseUri}/Workflows/launch`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getWorkflowDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<WorkflowDetails>>(`${baseUri}/Workflows/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getWorkflowActivityChecklists = async (workflowId: string, activityId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<WorkflowChecklistSummary[]>>(
    `${baseUri}/Workflows/${workflowId}/activities/${activityId}/checklists`,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const getWorkflowEtag = async (workflowId: string) => {
  const { data } = await axios.get<ApiResponse<string>>(`${baseUri}/Workflows/${workflowId}/etag`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getWorkflowTriggerEmailDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<WorkflowEmailDetails>>(`${baseUri}/Workflows/${id}/trigger-email`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createWorkflowActivityChangeSet = async (
  workflowId: string,
  activityId: string,
  payload: CreateWorkflowActivityChangeSetRequest
) => {
  const { data } = await axios.post<ApiResponse<WorkflowActivity>>(
    `${baseUri}/Workflows/${workflowId}/activities/${activityId}/preview-change-set`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateWorkflowActivityChangeSets = async (
  workflowId: string,
  activityId: string,
  payload: UpdateWorkflowActivityChangeSetsRequest
) => {
  const { data } = await axios.put<ApiResponse<WorkflowActivity>>(
    `${baseUri}/Workflows/${workflowId}/activities/${activityId}/preview-change-set`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const executeWorkflowAction = async (workflowId: string, payload: ExecuteWorkflowUserActionRequest) => {
  const { data } = await axios.post<ApiResponse<ExecuteWorkflowUserActionResponse>>(
    `${baseUri}/Workflows/${workflowId}/execute-action`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getWorkflowActionChecks = async (workflowId: string, signalType: WorkflowSignalType) => {
  const { data } = await axios.get<ApiResponse<PreconditionFailure[]>>(
    `${baseUri}/Workflows/${workflowId}/actions/${signalType}/checks`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const cancelWorkflow = async (workflowId: string, payload: CancelWorkflowRequest) => {
  const { data } = await axios.post<ApiResponse<ExecuteWorkflowUserActionResponse>>(
    `${baseUri}/Workflows/${workflowId}/cancel`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const restartWorkflow = async (workflowId: string, payload: RestartWorkflowRequest) => {
  const { data } = await axios.post<ApiResponse<WorkflowDetails>>(
    `${baseUri}/Workflows/${workflowId}/restart`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getWorkflowRestartChecks = async (workflowId: string) => {
  const { data } = await axios.get<ApiResponse<PreconditionFailure[]>>(
    `${baseUri}/Workflows/${workflowId}/restart/checks`,
    { headers: getDefaultHeaders(), timeout: 25_000 }
  );
  return data;
};

const reevaluateWorkflow = async (workflowId: string) => {
  const { data } = await axios.post<ApiResponse<Workflow>>(`${baseUri}/Workflows/${workflowId}/reevaluate`, null, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const retryWorkflow = async (workflowId: string) => {
  const { data } = await axios.post<ApiResponse<Workflow>>(`${baseUri}/Workflows/${workflowId}/retry`, null, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getNextWorkflowApproval = async (workflowId: string) => {
  const { data } = await axios.get<ApiResponse<NextWorkflowApproval>>(
    `${baseUri}/Workflows/${workflowId}/next-approval`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const submitWorkflowSupervisorResponse = async (
  workflowId: string,
  activityId: string,
  payload: SubmitWorkflowSupervisorResponseRequest
) => {
  const { data } = await axios.post<ApiResponse<SubmitWorkflowSupervisorResponseResult>>(
    `${baseUri}/Workflows/${workflowId}/activities/${activityId}/supervisor-response`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const uploadWorkflowAttachment = async (workflowId: string, file: File, { onboard }: { onboard: boolean }) => {
  const formData = new FormData();
  formData.append("file", file);
  if (onboard) {
    formData.append("onboard", "true");
  }

  const { data } = await axios.post<ApiResponse<UploadWorkflowAttachmentResult>>(
    `${baseUri}/Workflows/${workflowId}/attachment-files`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );
  return data;
};

const deleteWorkflowAttachment = async (workflowId: string, fileCatalogId: string) => {
  const { data } = await axios.delete<ApiResponse<Workflow>>(
    `${baseUri}/Workflows/${workflowId}/attachment-files/${fileCatalogId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getWorkflowDocumentFileDownloadInfo = async (workflowId: string, catalogueId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/Workflows/${workflowId}/document-files/${catalogueId}/download-info`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getWorkflowDocumentHtmlWithHighlights = async (
  workflowId: string,
  activityId: string,
  payload: DocumentHtmlWithHighlightsRequest,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<DocumentHtmlWithHighlightsResponse>>(
    `${baseUri}/Workflows/${workflowId}/activities/${activityId}/document-html-with-highlights`,
    payload,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const matchWorkflowDocuments = async (request: MatchDocumentsRequest) => {
  const { data } = await axios.post<ApiResponse<MatchDocumentsResponse>>(
    `${baseUri}/Workflows/match-documents`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const runDocumentWorkflows = async (request: RunDocumentWorkflowsRequest) => {
  const { data } = await axios.post<ApiResponse<RunDocumentWorkflowsResponse>>(
    `${baseUri}/Workflows/documents/run`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDocumentWorkflowBySourceId = async (sourceId: string) => {
  const { data } = await axios.get<ApiResponse<GetDocumentWorkflowBySourceResponse>>(
    `${baseUri}/Workflows/documents/by-source/${sourceId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const uploadDocumentWorkflowFile = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post<ApiResponse<FileInfo>>(`${baseUri}/Workflows/documents/files`, formData, {
    headers: getDefaultHeaders({ formData: true }),
  });
  return data;
};

// Workflow Templates

const getCurrentWorkflowTemplateVersions = async () => {
  const { data } = await axios.get<ApiResponse<WorkflowTemplateVersionInfo[]>>(
    `${baseUri}/WorkflowTemplateVersions/current`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const getWorkflowDefinitions = async () => {
  const { data } = await axios.get<ApiResponse<WorkflowDefinition[]>>(
    `${baseUri}/WorkflowTemplateVersions/definitions`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateWorkflowDefinitionStatus = async (
  workflowType: WorkflowType,
  payload: UpdateWorkflowDefinitionStatusRequest
) => {
  const { data } = await axios.patch<ApiResponse<WorkflowDefinition>>(
    `${baseUri}/WorkflowTemplateVersions/definitions/${workflowType}/status`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getWorkflowTemplateVersionDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<WorkflowTemplateVersionDetailsResponse>>(
    `${baseUri}/WorkflowTemplateVersions/${id}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateActivityInWorkflowTemplate = async (
  workflowTemplateVersionId: string,
  activityId: string,
  payload: UpdateWorkflowTemplateActivityRequest
) => {
  const { data } = await axios.patch<ApiResponse<WorkflowTemplateActivity>>(
    `${baseUri}/WorkflowTemplateVersions/${workflowTemplateVersionId}/activities/${activityId}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateWorkflowTemplateInstructions = async (
  workflowTemplateVersionId: string,
  payload: UpdateWorkflowTemplateInstructionsRequest
) => {
  const { data } = await axios.patch<ApiResponse<WorkflowTemplateVersionDetailsResponse>>(
    `${baseUri}/WorkflowTemplateVersions/${workflowTemplateVersionId}/workflow-instructions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getApprovalConfiguration = async (
  workflowTemplateVersionId: string,
  workflowActivityId: string,
  params?: { includeEmpty: boolean }
) => {
  const { data } = await axios.get<ApiResponse<ApprovalConfigurationVersion>>(
    `${baseUri}/WorkflowTemplateVersions/${workflowTemplateVersionId}/activities/${workflowActivityId}/approval-configuration`,
    { headers: getDefaultHeaders(), params }
  );
  return data;
};

const getCurrentWorkflowApprovalConfiguration = async (workflowType: WorkflowType) => {
  const { data } = await axios.get<ApiResponse<CurrentWorkflowApprovalConfigurationResponse>>(
    `${baseUri}/WorkflowTemplateVersions/current/${workflowType}/approval-configuration`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createApprovalConfigurationVersion = async (
  workflowTemplateVersionId: string,
  workflowActivityId: string,
  payload: CreateApprovalConfigurationVersionRequest
) => {
  const { data } = await axios.post<ApiResponse<ApprovalConfigurationVersion>>(
    `${baseUri}/WorkflowTemplateVersions/${workflowTemplateVersionId}/activities/${workflowActivityId}/approval-configuration`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Approvals

const getPendingApprovals = async () => {
  const { data } = await axios.get<ApiResponse<Approval[]>>(`${baseUri}/Approvals/pending`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getApprovalsRequestedByUser = async () => {
  const { data } = await axios.get<ApiResponse<Approval[]>>(`${baseUri}/Approvals/requested-by-user`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getApprovalsReviewedByUser = async ({ range, search }: { range: RangeRequestParams; search?: string }) => {
  const { data } = await axios.get<ApiResponse<ListWithTotal<Approval>>>(`${baseUri}/Approvals/reviewed-by-user`, {
    headers: getDefaultHeaders(),
    params: { ...range, search },
  });
  return data;
};

const getApproval = async (id: string) => {
  const { data } = await axios.get<ApiResponse<Approval>>(`${baseUri}/Approvals/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const approveApproval = async (id: string, payload: ApprovalActionRequest) => {
  const { data } = await axios.post<ApiResponse<Approval>>(`${baseUri}/Approvals/${id}/approve`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const rejectApproval = async (id: string, payload: ApprovalActionRequest) => {
  const { data } = await axios.post<ApiResponse<Approval>>(`${baseUri}/Approvals/${id}/reject`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const cancelApproval = async (id: string, payload: ApprovalActionRequest) => {
  const { data } = await axios.post<ApiResponse<Approval>>(`${baseUri}/Approvals/${id}/cancel`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const reassignApproval = async (id: string, payload: ReassignApprovalRequest) => {
  const { data } = await axios.post<ApiResponse<Approval>>(`${baseUri}/Approvals/${id}/reassign`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const approveBulkApprovals = async (payload: BulkApprovalActionRequest) => {
  const { data } = await axios.post<ApiResponse<Approval[]>>(`${baseUri}/Approvals/approve-bulk`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Object Management

const getOrganizationObjectDefinitions = async () => {
  const { data } = await axios.get<ApiResponse<ObjectClassDefinitionInfo[]>>(`${baseUri}/ObjectDefinitions`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const searchOrganizationObjectDefinitions = async (request: SearchObjectDefinitionsRequest) => {
  const { data } = await axios.post<ApiResponse<ObjectClassDefinitionInfo[]>>(
    `${baseUri}/ObjectDefinitions/search`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getOrganizationObjectDefinitionByObjectType = async (objectType: string) => {
  const { data } = await axios.get<ApiResponse<ObjectClassDefinition>>(`${baseUri}/ObjectDefinitions/${objectType}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateObjectDefinition = async (objectType: string, request: UpdateObjectDefinitionRequest) => {
  const { data } = await axios.patch<ApiResponse<ObjectClassDefinition>>(
    `${baseUri}/ObjectDefinitions/${objectType}`,
    request,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectDefinitionsByObjectTypes = async (
  objectTypes: string[]
): Promise<ApiResponse<Record<string, ObjectClassDefinition>>> => {
  const { data } = await axios.post<ApiResponse<Record<string, ObjectClassDefinition>>>(
    `${baseUri}/ObjectDefinitions/get-by-object-types`,
    { objectTypes },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createObjectField = async (objectType: string, payload: CreateFieldRequest) => {
  const { data } = await axios.post<ApiResponse<AddFieldToObjectClassDefinitionResponse>>(
    `${baseUri}/ObjectDefinitions/${objectType}/fields`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateObjectField = async (objectType: string, fieldId: string, payload: UpdateFieldRequest) => {
  const { data } = await axios.patch<ApiResponse<ObjectClassDefinition>>(
    `${baseUri}/ObjectDefinitions/${objectType}/fields/${encodeURIComponent(fieldId)}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteObjectField = async (objectType: string, fieldId: string) => {
  const { data } = await axios.delete<ApiResponse<ObjectClassDefinition>>(
    `${baseUri}/ObjectDefinitions/${objectType}/fields/${encodeURIComponent(fieldId)}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getObjectConfigurationLayout = async (objectType: string, configurationId?: string) => {
  const { data } = await axios.get<ApiResponse<PageLayout>>(`${baseUri}/ObjectDefinitions/${objectType}/layout`, {
    headers: getDefaultHeaders(),
    params: { configurationId },
  });
  return data;
};

const saveObjectConfigurationLayout = async (objectType: string, payload: CreateOrganizationPageLayoutRequest) => {
  const { data } = await axios.post<ApiResponse<PageLayout>>(
    `${baseUri}/ObjectDefinitions/${objectType}/layout`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const addObjectChecklist = async (objectType: string, payload: AddChecklistRequest) => {
  const { data } = await axios.post<ApiResponse<ObjectChecklist[]>>(
    `${baseUri}/ObjectDefinitions/${objectType}/checklists`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateObjectChecklist = async (objectType: string, checklistId: string, payload: UpdateChecklistRequest) => {
  const { data } = await axios.put<ApiResponse<ObjectChecklist[]>>(
    `${baseUri}/ObjectDefinitions/${objectType}/checklists/${checklistId}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteObjectChecklist = async (objectType: string, checklistId: string) => {
  const { data } = await axios.delete<ApiResponse<ObjectChecklist[]>>(
    `${baseUri}/ObjectDefinitions/${objectType}/checklists/${checklistId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Data Model Configuration

const getDataModelConfigurations = async () => {
  const { data } = await axios.get<ApiResponse<DataModelConfiguration[]>>(`${baseUri}/DataModelConfigurations`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createDataModelConfiguration = async (payload: CreateDataModelConfigurationRequest) => {
  const { data } = await axios.post<ApiResponse<DataModelConfiguration>>(
    `${baseUri}/DataModelConfigurations`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const updateDataModelConfiguration = async (id: string, payload: UpdateDataModelConfigurationRequest) => {
  const { data } = await axios.patch<ApiResponse<DataModelConfiguration>>(
    `${baseUri}/DataModelConfigurations/${id}`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const deleteDataModelConfiguration = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/DataModelConfigurations/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const assignClientToDataModelConfiguration = async (payload: AssignClientToConfigurationRequest) => {
  const { data } = await axios.post<ApiResponse<null>>(`${baseUri}/DataModelConfigurations/assign-client`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Document Activity

const searchDocumentViews = async (payload: SearchDocumentViewsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<DocumentViews>>>(
    `${baseUri}/DocumentViews/search`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const exportDocumentViewsToExcel = async (payload: SearchDocumentViewsRequest, fileName?: string) => {
  const { data } = await axios.post<ApiResponse<DocumentViewsExport>>(
    `${baseUri}/DocumentViews/excel-export`,
    payload,
    {
      headers: getDefaultHeaders(),
      params: { fileName },
    }
  );
  return data;
};

const getAggregatedDocumentViewsByContact = async (attachmentId: string, contactId: string) => {
  const { data } = await axios.get<ApiResponse<AttachmentAggregatedDocumentViews[]>>(
    `${baseUri}/DocumentViews/attachments/${attachmentId}/aggregate/by-contact/${contactId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getAggregatedFundraisingDocumentViewsByContact = async (fundraisingDocumentId: string, contactId: string) => {
  const { data } = await axios.get<ApiResponse<FundraisingAggregatedDocumentViews[]>>(
    `${baseUri}/DocumentViews/fundraising-documents/${fundraisingDocumentId}/aggregate/by-contact/${contactId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getAggregatedDocumentViewsByInvestor = async (attachmentId: string, investorId: string) => {
  const { data } = await axios.get<ApiResponse<AttachmentAggregatedDocumentViews[]>>(
    `${baseUri}/DocumentViews/attachments/${attachmentId}/aggregate/by-investor/${investorId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getAggregatedFundraisingDocumentViewsByInvestor = async (fundraisingDocumentId: string, investorId: string) => {
  const { data } = await axios.get<ApiResponse<FundraisingAggregatedDocumentViews[]>>(
    `${baseUri}/DocumentViews/fundraising-documents/${fundraisingDocumentId}/aggregate/by-investor/${investorId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getMessageAttachmentInfo = async (attachmentId: string, params: { contactId?: string; investorId?: string }) => {
  const { data } = await axios.get<ApiResponse<DocumentPresentationInfo>>(
    `${baseUri}/DocumentViews/attachments/${attachmentId}/presentation-info`,
    { params, headers: getDefaultHeaders() }
  );
  return data;
};

const getFundraisingDocumentInfo = async (
  fundraisingId: string,
  fundraisingDocumentId: string,
  params: { contactId?: string; investorId?: string }
) => {
  const { data } = await axios.get<ApiResponse<DocumentPresentationInfo>>(
    `${baseUri}/DocumentViews/fundraising/${fundraisingId}/documents/${fundraisingDocumentId}/presentation-info`,
    { params, headers: getDefaultHeaders() }
  );
  return data;
};

// Fundraising

const getFundraisings = async (status?: FundraisingStatus[]) => {
  const { data } = await axios.get<ApiResponse<FundraisingInfo[]>>(`${baseUri}/Fundraisings`, {
    params: { status },
    headers: getDefaultHeaders(),
  });

  return data;
};

const getFundraisingById = async (id: string) => {
  const { data } = await axios.get<ApiResponse<Fundraising>>(`${baseUri}/Fundraisings/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getFundraisingAccess = async (fundId: string) => {
  const { data } = await axios.get<ApiResponse<FundraisingAccessItem[]>>(`${baseUri}/Fundraisings/access`, {
    params: { fundId },
    headers: getDefaultHeaders(),
  });
  return data;
};

const getFundraisingAccessByFundIdAsExcelFile = async (fundId: string, fileName: string) => {
  const resp = await axios.get<string>(`${baseUri}/Fundraisings/access/excel-file`, {
    params: { fundId, fileName },
    headers: getDefaultHeaders(),
    responseType: "blob",
  });
  return resp;
};

const createFundraising = async (payload: CreateFundraisingRequest) => {
  const { data } = await axios.post<ApiResponse<Fundraising>>(`${baseUri}/Fundraisings`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateFundraising = async (id: string, payload: UpdateFundraisingRequest) => {
  const { data } = await axios.patch<ApiResponse<Fundraising>>(`${baseUri}/Fundraisings/${id}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateFundraisingAccess = async (id: string, payload: UpdateFundraisingAccessRequest) => {
  const { data } = await axios.patch<ApiResponse<UpdateFundraisingAccessResponse>>(
    `${baseUri}/Fundraisings/${id}/access`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const deleteFundraising = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/Fundraisings/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const uploadFundraisingFile = async (fundraisingId: string, payload: UploadFundraisingFileRequest) => {
  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("expectedEtag", payload.expectedEtag);
  if (payload.externalCategoryId) {
    formData.append("externalCategoryId", payload.externalCategoryId);
  }
  if (payload.section) {
    formData.append("section", payload.section);
  }

  const { data } = await axios.post<ApiResponse<UploadFundraisingFileResponse>>(
    `${baseUri}/Fundraisings/${fundraisingId}/files`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );
  return data;
};

const getFundraisingFileDownloadInfo = async (fundraisingId: string, fileId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/Fundraisings/${fundraisingId}/files/${fileId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDownloadUrlForMultipleFundraisingFiles = async (
  fundraisingId: string,
  fileName: string,
  fileIds: string[]
) => {
  const { data } = await axios.post<ApiResponse<string>>(
    `${baseUri}/Fundraisings/${fundraisingId}/files/token`,
    { fileIds },
    { headers: getDefaultHeaders() }
  );

  const token = data.data;
  return token
    ? createApiResponse(
        `${baseUri}/Fundraisings/multiple-files?${qs.stringify({ fileName, token, clientCode: getClientCode() })}`
      )
    : createErrorApiResponse<"">(data.error);
};

const getDownloadUrlForMultipleFilesFromFundraisings = async (
  fundraisingDocumentIdentifiers: FundraisingDocumentIdentifier[],
  fileName: string
) => {
  const { data } = await axios.post<ApiResponse<string>>(
    `${baseUri}/Fundraisings/files/token`,
    { fundraisingDocumentIdentifiers },
    { headers: getDefaultHeaders() }
  );

  const token = data.data;
  return token
    ? createApiResponse(
        `${baseUri}/Fundraisings/multiple-files?${qs.stringify({ fileName, token, clientCode: getClientCode() })}`
      )
    : createErrorApiResponse<"">(data.error);
};

const sendTestFundraisingEmail = async (fundraisingId: string, payload: SendTestFundraisingEmailRequest) => {
  const { data } = await axios.post<ApiResponse<null>>(
    `${baseUri}/Fundraisings/${fundraisingId}/send-test-email`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getTestEmailContactsForFundraising = async (fundraisingId: string) => {
  const { data } = await axios.get<ApiResponse<GetTestEmailContactsForFundraisingResponse>>(
    `${baseUri}/Fundraisings/${fundraisingId}/test-email-contacts`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getFundraisingJobProgress = async (asyncOperationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(`${baseUri}/Fundraisings/jobs/${asyncOperationId}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createFundraisingCategory = async (payload: CreateFundraisingCategoryRequest) => {
  const { data } = await axios.post<ApiResponse<Category>>(`${baseUri}/FundraisingCategories`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateFundraisingCategory = async (id: string, payload: UpdateFundraisingCategoryRequest) => {
  const { data } = await axios.patch<ApiResponse<Category>>(`${baseUri}/FundraisingCategories/${id}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteFundraisingCategory = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/FundraisingCategories/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Portfolio Monitoring

const getAllMetrics = async (inactive?: boolean) => {
  const { data } = await axios.get<ApiResponse<Metric[]>>(`${baseUri}/Metrics`, {
    headers: getDefaultHeaders(),
    params: { inactive },
  });
  return data;
};

const getMetric = async (id: string) => {
  const { data } = await axios.get<ApiResponse<Metric>>(`${baseUri}/Metrics/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createMetric = async (payload: CreateMetricRequest) => {
  const { data } = await axios.post<ApiResponse<Metric>>(`${baseUri}/Metrics`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateMetric = async (id: string, payload: UpdateMetricRequest) => {
  const { data } = await axios.patch<ApiResponse<Metric>>(`${baseUri}/Metrics/${id}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteMetric = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/Metrics/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Data Collection

const searchDataSubmissions = async (payload: SearchSubmissionsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<DataCollectionSubmissionInfo>>>(
    `${baseUri}/DataSubmissions/search`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getAssignedDataSubmissions = async () => {
  const { data } = await axios.get<ApiResponse<DataCollectionSubmissionInfo[]>>(`${baseUri}/DataSubmissions/assigned`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getDataSubmissionDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<DataCollectionSubmissionDetails>>(`${baseUri}/DataSubmissions/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const saveSubmissionLayoutBlockInput = async (
  submissionId: string,
  blockId: string,
  payload: SaveSubmissionLayoutBlockInputRequest
) => {
  const { data } = await axios.put<ApiResponse<DataCollectionSubmissionDetails>>(
    `${baseUri}/DataSubmissions/${submissionId}/blocks/${blockId}`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const submitDataSubmission = async (id: string) => {
  const { data } = await axios.post<ApiResponse<null>>(`${baseUri}/DataSubmissions/${id}/submit`, null, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const exportSubmissionBlockInputToExcel = async (submissionId: string, blockId: string) => {
  const { data } = await axios.post<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DataSubmissions/${submissionId}/blocks/${blockId}/excel-export`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const importSubmissionBlockInputFromExcel = async (submissionId: string, blockId: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post<ApiResponse<ImportSubmissionBlockInputResponse>>(
    `${baseUri}/DataSubmissions/${submissionId}/blocks/${blockId}/excel-import`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );
  return data;
};

const searchObjectMetricExtensions = async (payload: SearchObjectMetricExtensionsRequest) => {
  const { data } = await axios.post<ApiResponse<ObjectMetricExtension[]>>(
    `${baseUri}/ObjectMetricExtensions/search`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createObjectMetricExtensions = async (payload: CreateMultipleObjectMetricExtensionsRequest) => {
  const { data } = await axios.post<ApiResponse<ObjectMetricExtension[]>>(
    `${baseUri}/ObjectMetricExtensions/create-multiple`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateObjectMetricExtensions = async (payload: UpdateMultipleMetricExtensionsRequest) => {
  const { data } = await axios.patch<ApiResponse<ObjectMetricExtension[]>>(
    `${baseUri}/ObjectMetricExtensions/update-multiple`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const renameObjectMetricExtension = async (payload: RenameMetricExtensionRequest) => {
  const { data } = await axios.put<ApiResponse<ObjectMetricExtension>>(
    `${baseUri}/ObjectMetricExtensions/rename`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const searchDataCollectionRequests = async (payload: SearchDataCollectionRequestRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<DataCollectionRequestInfo>>>(
    `${baseUri}/DataCollectionRequests/search`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataCollectionRequestDetails = async (id: string) => {
  const { data } = await axios.get<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createDataCollectionRequest = async (payload: CreateDataCollectionRequestRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateDataCollectionRequest = async (id: string, payload: UpdateDataCollectionRequestRequest) => {
  const { data } = await axios.patch<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteDataCollectionRequest = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/DataCollectionRequests/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const publishDataCollectionRequest = async (id: string, payload: PublishRequestRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/publish`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const cancelScheduledPublishOfDataRequest = async (id: string) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/cancel-scheduled-publish`,
    null,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const addSubmissionsToDataRequest = async (id: string, payload: AddRequestSubmissionsRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/add-submissions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const publishDataRequestSubmissions = async (id: string, payload: RequestSubmissionOperationRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/publish-submissions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const unpublishDataRequestSubmissions = async (id: string, payload: RequestSubmissionOperationRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/unpublish-submissions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const requestChangesForDataRequestSubmissions = async (id: string, payload: RequestSubmissionOperationRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/request-submission-changes`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteDataRequestSubmissions = async (id: string, payload: RequestSubmissionOperationRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/delete-submissions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const sendSubmissionReminders = async (id: string, payload: RequestSubmissionOperationRequest) => {
  const { data } = await axios.post<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/send-submission-reminders`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateDataRequestSubmissions = async (id: string, payload: UpdateRequestSubmissionsRequest) => {
  const { data } = await axios.patch<ApiResponse<DataCollectionRequestDetails>>(
    `${baseUri}/DataCollectionRequests/${id}/update-submissions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataRequestTemplates = async () => {
  const { data } = await axios.get<ApiResponse<DataCollectionRequestTemplateVersion[]>>(
    `${baseUri}/DataCollectionRequestTemplates`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getLatestRequestTemplateVersion = async (templateId: string) => {
  const { data } = await axios.get<ApiResponse<DataCollectionRequestTemplateVersion>>(
    `${baseUri}/DataCollectionRequestTemplates/${templateId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createDataRequestTemplateVersion = async (payload: CreateDataCollectionRequestTemplateVersionRequest) => {
  const { data } = await axios.post<ApiResponse<CreateDataCollectionRequestTemplateVersionResponse>>(
    `${baseUri}/DataCollectionRequestTemplates`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getDataSubmissionConfigurations = async () => {
  const { data } = await axios.get<ApiResponse<DataSubmissionConfiguration[]>>(
    `${baseUri}/DataCollectionRequestTemplates/configurations`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Expense Management

const getExpenseManagementInboxAddresses = async () => {
  const { data } = await axios.get<ApiResponse<ExpenseManagementInboxAddressesResponse>>(
    `${baseUri}/ExpenseManagement/inbox-addresses`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvoiceDocumentsDownload = async (invoiceIds: string[], fileName: string) => {
  const { data } = await axios.post<ApiResponse<GetInvoiceDocumentsDownloadTokenResponse>>(
    `${baseUri}/ExpenseManagement/invoice-documents/token`,
    { invoiceIds },
    { headers: getDefaultHeaders() }
  );

  if (!data.success) {
    return createErrorApiResponse<InvoiceDocumentsDownload>(data.error);
  }

  const query = qs.stringify({ fileName, token: data.data.token, clientCode: getClientCode() });

  return createApiResponse<InvoiceDocumentsDownload>({
    downloadUrl: `${baseUri}/ExpenseManagement/invoice-documents?${query}`,
    requestedInvoiceCount: data.data.requestedInvoiceCount,
    includedDocumentCount: data.data.includedDocumentCount,
  });
};

const getInvoiceApproversAssignment = async (invoiceIds: string[]) => {
  const { data } = await axios.post<ApiResponse<GetInvoiceApproversAssignmentResponse>>(
    `${baseUri}/ExpenseManagement/invoice-actions/approvers-assignment`,
    { invoiceIds },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvoiceActionChecks = async (payload: GetInvoiceActionChecksRequest) => {
  const { data } = await axios.post<ApiResponse<GetInvoiceActionChecksResponse>>(
    `${baseUri}/ExpenseManagement/invoice-actions/checks`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const executeInvoiceAction = async (payload: ExecuteInvoiceActionRequest) => {
  const { data } = await axios.post<ApiResponse<ExecuteInvoiceActionResponse>>(
    `${baseUri}/ExpenseManagement/invoice-actions`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const previewInvoiceCard = async (invoiceId: string, payload: PreviewInvoiceCardRequest) => {
  const { data } = await axios.post<ApiResponse<PreviewInvoiceCardResponse>>(
    `${baseUri}/ExpenseManagement/invoices/${invoiceId}/card-preview`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvoiceSource = async (invoiceId: string) => {
  const { data } = await axios.get<ApiResponse<GetInvoiceSourceResponse>>(
    `${baseUri}/ExpenseManagement/invoices/${invoiceId}/source`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvoiceAgent = async (invoiceId: string) => {
  const { data } = await axios.get<ApiResponse<GetInvoiceAgentResponse>>(
    `${baseUri}/ExpenseManagement/invoices/${invoiceId}/invoice-agent`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// BC Access

const ensureBusinessCentralAccessIsEnabled = async (role?: BcRole) => {
  const { data } = await axios.post<ApiResponse<{ jobStarted: boolean; jobId?: string }>>(
    `${baseUri}/BusinessCentralAccess/ensure-access`,
    { role: role ?? "" },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getBusinessCentralAccessJobStatus = async (jobId: string) => {
  const { data } = await axios.get<ApiResponse<{ finished: boolean }>>(
    `${baseUri}/BusinessCentralAccess/jobs/${jobId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const resolveFundOpsTransactionLink = async (no: string) => {
  const { data } = await axios.get<ApiResponse<{ resolvedUrl: string }>>(`${baseUri}/FundOpsLinks/resolve`, {
    params: { no },
    headers: getDefaultHeaders(),
  });
  return data;
};

// Video Library

const getVideoLibraryCategories = async () => {
  const { data } = await axios.get<ApiResponse<VideoLibraryCategory[]>>(`${baseUri}/VideoLibrary/categories`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getVideosFromLibrary = async () => {
  const { data } = await axios.get<ApiResponse<VideoLibraryItem[]>>(`${baseUri}/VideoLibrary/videos`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getVideoItemFromLibrary = async (id: string) => {
  const { data } = await axios.get<ApiResponse<VideoLibraryItem>>(`${baseUri}/VideoLibrary/videos/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const addVideoToLibrary = async (payload: AddVideoRequest) => {
  const { data } = await axios.post<ApiResponse<VideoLibraryItem>>(`${baseUri}/VideoLibrary/videos`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateVideoInLibrary = async (id: string, payload: UpdateVideoRequest) => {
  const { data } = await axios.patch<ApiResponse<VideoLibraryItem>>(`${baseUri}/VideoLibrary/videos/${id}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteVideoFromLibrary = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/VideoLibrary/videos/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getUploadUrlForVideo = async (payload: GetVideoUploadUrlRequest) => {
  const { data } = await axios.post<ApiResponse<GetVideoUploadUrlResponse>>(
    `${baseUri}/VideoLibrary/videos/get-upload-url`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const completeVideoUpload = async (payload: CompleteVideoUploadRequest) => {
  const { data } = await axios.post<ApiResponse<VideoLibraryItem>>(
    `${baseUri}/VideoLibrary/videos/complete-upload`,
    payload,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

// Inboxes

const searchInboxes = async (request: SearchInboxesRequest) => {
  const { data } = await axios.post<ApiResponse<Inbox[]>>(`${baseUri}/Inboxes/search`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getInbox = async (id: string) => {
  const { data } = await axios.get<ApiResponse<Inbox>>(`${baseUri}/Inboxes/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const createInbox = async (request: CreateInboxRequest) => {
  const { data } = await axios.post<ApiResponse<Inbox>>(`${baseUri}/Inboxes`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateInbox = async (id: string, request: UpdateInboxRequest) => {
  const { data } = await axios.patch<ApiResponse<Inbox>>(`${baseUri}/Inboxes/${id}`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteInbox = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(`${baseUri}/Inboxes/${id}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getAgents = async () => {
  const { data } = await axios.get<ApiResponse<AgentInfo[]>>(`${baseUri}/Inboxes/agents`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

// Dashboards

const getDashboards = async (signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<GetAllDashboardsResult>>(`${baseUri}/Dashboards`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const getDashboard = async (dashboardId: string) => {
  const { data } = await axios.get<ApiResponse<GetDashboardResult>>(`${baseUri}/Dashboards/${dashboardId}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const addDashboardDraft = async () => {
  const { data } = await axios.post<ApiResponse<AddDashboardResult>>(`${baseUri}/Dashboards/draft`, null, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const updateDashboard = async (dashboardId: string, dashboard: Dashboard) => {
  const payload = { dashboard };
  const { data } = await axios.put<ApiResponse<GetDashboardResult>>(`${baseUri}/Dashboards/${dashboardId}`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const deleteDashboards = async (dashboardIds: string[]) => {
  const { data } = await axios.delete<ApiResponse<boolean>>(`${baseUri}/Dashboards/delete`, {
    headers: getDefaultHeaders(),
    data: { dashboardIds },
  });
  return data;
};

const duplicateDashboard = async (request: DuplicateDashboardRequest) => {
  const { data } = await axios.post<ApiResponse<DuplicateDashboardResult>>(`${baseUri}/Dashboards/duplicate`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const copyDashboard = (
  request: CopyDashboardRequest,
  done: (data: ApiResponse<CopyDashboardResult>) => void,
  error: (reason: string) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<CopyDashboardResult>>(`${baseUri}/Dashboards/copy`, request, {
      headers: getDefaultHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => error(reason.message));

  return { cancel: () => controller.abort() };
};

// Activity Log

const getActivityLogExportOptions = async () => {
  const { data } = await axios.get<ApiResponse<ActivityLogExportOption[]>>(`${baseUri}/ActivityLogExport/options`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const startActivityLogExport = async (payload: ActivityLogExportRequest) => {
  const { data } = await axios.post<ApiResponse<JobStartResult>>(`${baseUri}/ActivityLogExport/start`, payload, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getActivityLogExportJobProgress = async (operationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(`${baseUri}/ActivityLogExport/${operationId}/progress`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const startOrganizationContactsActivityLogExport = async () => {
  const { data } = await axios.post<ApiResponse<JobStartResult>>(
    `${baseUri}/ActivityLogExport/organization-contacts/start`,
    undefined,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getOrganizationContactsActivityLogExportJobProgress = async (operationId: string) => {
  const { data } = await axios.get<ApiResponse<JobProgress>>(
    `${baseUri}/ActivityLogExport/organization-contacts/${operationId}/progress`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const searchActivityLogs = async (payload: SearchActivityLogsRequest) => {
  const { data } = await axios.post<ApiResponse<PaginatedList<ActivityLogRecord>>>(
    `${baseUri}/ActivityLog/search`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

// Investor Communications

const searchInvestorCommunications = async (payload: SearchInvestorCommunicationsRequest) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<InvestorCommunicationInfo>>>(
    `${baseUri}/InvestorCommunications/search`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvestorCommunicationById = async (communicationId: string) => {
  const { data } = await axios.get<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvestorCommunicationRecipients = async (communicationId: string) => {
  const { data } = await axios.get<ApiResponse<InvestorCommunicationRecipient[]>>(
    `${baseUri}/InvestorCommunications/${communicationId}/recipients`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvestorCommunicationEngagement = async (communicationId: string) => {
  const { data } = await axios.get<ApiResponse<InvestorCommunicationEngagement>>(
    `${baseUri}/InvestorCommunications/${communicationId}/engagement`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getInvestorCommunicationHeroImageDownloadInfo = async (communicationId: string) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/InvestorCommunications/${communicationId}/article/hero-image`,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const createInvestorCommunication = async (type: InvestorCommunicationType) => {
  const { data } = await axios.post<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications`,
    { type },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const updateInvestorCommunication = async (communicationId: string, payload: UpdateInvestorCommunicationRequest) => {
  const { data } = await axios.patch<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const uploadInvestorCommunicationHeroImage = async (communicationId: string, file: File, expectedEtag: string) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("expectedEtag", expectedEtag);

  const { data } = await axios.post<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}/article/hero-image`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );
  return data;
};

const uploadInvestorCommunicationBodyImage = async (communicationId: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post<ApiResponse<InvestorCommunicationBodyImage>>(
    `${baseUri}/InvestorCommunications/${communicationId}/article/body-images`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );
  return data;
};

const deleteInvestorCommunicationHeroImage = async (communicationId: string, expectedEtag: string) => {
  const { data } = await axios.delete<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}/article/hero-image`,
    { params: { expectedEtag }, headers: getDefaultHeaders() }
  );
  return data;
};

const getInvestorCommunicationVideoUploadUrl = async (communicationId: string, fileName: string) => {
  const { data } = await axios.post<ApiResponse<InvestorCommunicationVideoUploadInfo>>(
    `${baseUri}/InvestorCommunications/${communicationId}/video/upload-url`,
    { fileName },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const completeInvestorCommunicationVideoUpload = async (
  communicationId: string,
  fileId: string,
  expectedEtag: string
) => {
  const { data } = await axios.post<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}/video/complete-upload`,
    { fileId, expectedEtag },
    { headers: getDefaultHeaders() }
  );
  return data;
};

const uploadInvestorCommunicationVideoThumbnail = async (communicationId: string, file: File, expectedEtag: string) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("expectedEtag", expectedEtag);

  const { data } = await axios.post<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}/video/thumbnail`,
    formData,
    { headers: getDefaultHeaders({ formData: true }) }
  );
  return data;
};

const deleteInvestorCommunicationVideo = async (communicationId: string, expectedEtag: string) => {
  const { data } = await axios.delete<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}/video`,
    { params: { expectedEtag }, headers: getDefaultHeaders() }
  );
  return data;
};

const deleteInvestorCommunicationVideoThumbnail = async (communicationId: string, expectedEtag: string) => {
  const { data } = await axios.delete<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}/video/thumbnail`,
    { params: { expectedEtag }, headers: getDefaultHeaders() }
  );
  return data;
};

const changeInvestorCommunicationStatus = async (
  communicationId: string,
  payload: ChangeInvestorCommunicationStatusRequest
) => {
  const { data } = await axios.post<ApiResponse<InvestorCommunication>>(
    `${baseUri}/InvestorCommunications/${communicationId}/change-status`,
    payload,
    { headers: getDefaultHeaders() }
  );
  return data;
};

const deleteInvestorCommunication = async (communicationId: string) => {
  const { data } = await axios.delete<ApiResponse<null>>(`${baseUri}/InvestorCommunications/${communicationId}`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const getResourceChangesSubscriptionToken = async () => {
  const { data } = await axios.get<ApiResponse<string>>(`${baseUri}/ResourceChanges/subscription-token`, {
    headers: getDefaultHeaders(),
  });
  return data;
};

const adminApi = {
  getIdentity,
  updateUserConsentAgreements,
  updateUserPreferences,
  getUserPermissions,
  getClientSettings,
  getClientDictionaries,
  getMenuBadges,
  getFundAccountingAppsStatus,
  resendInvitationEmailByMessage,
  resetContactPassword,
  resumeContactEmailDelivery,
  changeContactEmailAndFundPortalLogin,
  getSignOnSettings,
  updateSignOnSettings,
  getDocumentCollections,
  getDocumentCollectionDetails,
  getDocumentCollectionEmails,
  getDocCollectionFileDownloadInfo,
  getDownloadUrlForDocumentCollection,
  getDownloadUrlForSelectedDocuments,
  changeDocumentCollectionsStatus,
  changeDocumentCollectionDocumentsStatus,
  changeDocumentCollectionDocumentsAccess,
  exportDocumentCollectionsToExcel,
  sendInvitations,
  searchInvestorSentEmails,
  startInvestorSentEmailsExport,
  getInvestorSentEmailsExportProgress,
  searchPortfolioSentEmails,
  startPortfolioSentEmailsExport,
  getPortfolioSentEmailsExportProgress,
  getOrganizationManagedCompanies,
  getOrganizationCompaniesEntities,
  getOrganizationMembers,
  getOrganizationContacts,
  updateOrganizationContacts,
  getOrganizationDeactivatedMembers,
  getOrganizationMembersInvitations,
  deleteOrganizationMember,
  deleteOrganizationUserInvite,
  resendOrganizationUserInvite,
  updateOrganizationMemberAccess,
  getClientUserGroups,
  getClientUserGroupDetails,
  createClientUserGroup,
  updateClientUserGroup,
  deleteClientUserGroup,
  getAvailableClientUsers,
  getUserConsents,
  getUserConsentDetails,
  createUserConsent,
  updateUserConsent,
  deleteUserConsent,
  exportUserConsentAgreementsToExcel,
  convertUserConsentContentFileToHtml,
  updateCompanySettings,
  startDemoRefresh,
  getLatestDemoRefresh,
  getDemoRefreshTemplate,
  sendInvitationToContacts,
  startDeleteContacts,
  cancelContactsJob,
  getContactsJobProgress,
  exportContactsToExcel,
  getContactDetails,
  getContactPortalAccess,
  getContactEmailAvailability,
  getEmailRegistrationEligibility,
  createContact,
  deleteContact,
  getAccessCategories,
  getAccessCategoriesOfTypes,
  createAccessCategory,
  updateAccessCategory,
  getContactDocuments,
  getContactFundraisingDocuments,
  getContactEmails,
  getInvestorDetails,
  createInvestor,
  searchFunds,
  getFundFieldValues,
  updateFundFieldValues,
  searchInvestors,
  getInvestorFieldValues,
  updateInvestorFieldValues,
  searchContacts,
  getContactFieldValues,
  updateContactFieldValues,
  getObjectDefinition,
  getAvailableObjectDefinitions,
  searchObjects,
  startExportObjectsToExcel,
  getObjectsExcelExportJobProgress,
  getObjectById,
  getObjectEtag,
  getObjectDuplicates,
  createObject,
  updateObject,
  deleteObject,
  updateObjectLogo,
  removeObjectLogo,
  validateUniqueField,
  getLookupOptions,
  getRelatedObjects,
  getObjectsAsLookupReferences,
  countObjectsByType,
  changeChecklistTaskStatuses,
  updateChecklistTaskDetails,
  getObjectInternalUserPermissions,
  updateObjectInternalUserPermissions,
  getObjectInternalUsers,
  getAllContactsForObjectType,
  createObjectContact,
  getObjectContacts,
  updateObjectContacts,
  getObjectAccessCategories,
  createObjectAccessCategory,
  updateObjectAccessCategory,
  getCardLayout,
  getFundDetails,
  getFundPermissions,
  searchFundDocuments,
  exportFundDocumentsToExcel,
  getFundDocumentsDownloadToken,
  updateFundCommunicationMatrix,
  createAssistantThread,
  uploadAssistantAttachment,
  getAssistantAttachmentDownload,
  getAssistantChat,
  getAssistantChats,
  deleteAssistantChat,
  renameAssistantChat,
  getAssistantTasksOverview,
  getAssistantTask,
  renameAssistantTask,
  dismissAssistantTaskProblem,
  pauseAssistantTask,
  resumeAssistantTask,
  runAssistantTask,
  startAssistantTaskChat,
  deleteAssistantTask,
  startAssistantRun,
  getAssistantRunEvents,
  stopAssistantRun,
  approveAssistantAction,
  rejectAssistantAction,
  getAssistantSubscriptionToken,
  searchInvestorDocuments,
  exportInvestorDocumentsToExcel,
  getInvestorDocumentsDownloadToken,
  createArchiveDownloadUrl,
  getInvestorFundraisingDocuments,
  getInvestorPermissions,
  getInvestorInvestments,
  getInvestorCashFlows,
  updateInvestorCommunicationMatrix,
  createFundInvestor,
  updateFundInvestor,
  getNotes,
  createNote,
  updateNote,
  deleteNote,
  getFilesViews,
  getFileTagSettings,
  createFileTag,
  updateFileTag,
  deleteFileTag,
  getCompanyFiles,
  getFileDownloadInfo,
  getDownloadUrlForMultipleFiles,
  renameFile,
  deleteFiles,
  getFileDetails,
  getFilesViewsForEntityType,
  getFileTagsForEntityType,
  getEntityFiles,
  getEntityFileDownloadInfo,
  getDownloadUrlForMultipleEntityFiles,
  renameEntityFile,
  detachEntityFiles,
  getEntityFileDetails,
  uploadEntityFile,
  getDwhShares,
  postDwhShare,
  patchDwhShare,
  deleteDwhShare,
  getDwhLogs,
  getDwhLastSync,
  startDwhFullSync,
  getApiGatewayEligibleUsers,
  getApiGatewaySubscriptions,
  addApiGatewaySubscription,
  updateApiGatewaySubscription,
  deleteApiGatewaySubscription,
  getApiGatewayLogs,
  getFundPortalCoreBrandingSettings,
  updateFundPortalCoreBrandingSettings,
  getInvestorPortalSettings,
  updateInvestorPortalSettings,
  sendTestInvitationEmail,
  getTestInvitationContacts,
  getWatermarkSettings,
  updateWatermarkSettings,
  getInvestorPortalSyncStatus,
  getPortalWidgetSettings,
  updatePortalWidget,
  getDataBackupConnectionState,
  getDataBackupConfiguration,
  makeDataBackupTestConnection,
  setDataBackupDailySync,
  getDataBackupConfigurationOptions,
  setDataBackupConfiguration,
  runDataBackupNow,
  getDataBackupLogs,
  getPassthroughConfiguration,
  makePassthroughTestConnection,
  getPassthroughConnectionState,
  togglePassthroughIntegration,
  updatePassthroughConfiguration,
  getPassthroughLogs,
  searchDataImports,
  getDataImportDetails,
  getDataImportOptions,
  createDataImport,
  getCsvTemplateDownloadUrl,
  getDataExportDownloadUrls,
  uploadDataImportSourceFile,
  validateDataImportFileContent,
  startDataImportParsingAndValidation,
  getDataImportValidationStatus,
  getDataImportSourceFiles,
  getDataImportSourceFileDownloadInfo,
  getDataImportCsvFileDownloadInfo,
  getDataImportValidationSummaryCsvFileDownloadInfo,
  discardDataImport,
  searchDataImportChanges,
  getDataImportChangesTotals,
  getDataImportMetadata,
  startApplyingDataImport,
  getDataImportApplicationStatus,
  startDataImportRollback,
  getDataImportRollbackStatus,
  addDataImportChange,
  updateDataImportChange,
  excludeDataImportChange,
  revertExcludedDataImportChange,
  excludeChangesWithErrors,
  getDataImportFileContainers,
  createDataImportFileContainer,
  uploadFileIntoDataImportFileContainer,
  getUploadZipUrlForDataImportFileContainer,
  startUnzipForDataImportFileContainer,
  getUnzipJobProgressForDataImportFileContainer,
  deleteDataImportFileContainer,
  getFileContainerFileDownloadInfo,
  getPassthroughFunds,
  getPassthroughFundClosings,
  getInternalEntities,
  importPassthroughData,
  getPassthroughConnectionStatus,
  searchInvestorChangeRequests,
  getInvestorChangeRequestDetails,
  getMemberContributionAttachmentDownloadInfo,
  getInvestorChangeRequestAttachmentDownloadInfo,
  approveInvestorChangeRequest,
  rejectInvestorChangeRequest,
  retryInvestorChangeRequest,
  getWorkflowGroups,
  getWorkflowGroup,
  searchWorkflows,
  launchWorkflow,
  getWorkflowDetails,
  getWorkflowActivityChecklists,
  getWorkflowEtag,
  getWorkflowTriggerEmailDetails,
  createWorkflowActivityChangeSet,
  updateWorkflowActivityChangeSets,
  executeWorkflowAction,
  getWorkflowActionChecks,
  cancelWorkflow,
  restartWorkflow,
  getWorkflowRestartChecks,
  reevaluateWorkflow,
  retryWorkflow,
  getNextWorkflowApproval,
  submitWorkflowSupervisorResponse,
  uploadWorkflowAttachment,
  deleteWorkflowAttachment,
  getWorkflowDocumentFileDownloadInfo,
  getWorkflowDocumentHtmlWithHighlights,
  matchWorkflowDocuments,
  runDocumentWorkflows,
  getDocumentWorkflowBySourceId,
  uploadDocumentWorkflowFile,
  getCurrentWorkflowTemplateVersions,
  getWorkflowDefinitions,
  updateWorkflowDefinitionStatus,
  getWorkflowTemplateVersionDetails,
  updateActivityInWorkflowTemplate,
  updateWorkflowTemplateInstructions,
  getApprovalConfiguration,
  getCurrentWorkflowApprovalConfiguration,
  createApprovalConfigurationVersion,
  getPendingApprovals,
  getApprovalsRequestedByUser,
  getApprovalsReviewedByUser,
  getApproval,
  approveApproval,
  rejectApproval,
  cancelApproval,
  reassignApproval,
  approveBulkApprovals,
  getOrganizationObjectDefinitions,
  searchOrganizationObjectDefinitions,
  getOrganizationObjectDefinitionByObjectType,
  updateObjectDefinition,
  getObjectDefinitionsByObjectTypes,
  createObjectField,
  updateObjectField,
  deleteObjectField,
  getObjectConfigurationLayout,
  saveObjectConfigurationLayout,
  addObjectChecklist,
  updateObjectChecklist,
  deleteObjectChecklist,
  getDataModelConfigurations,
  createDataModelConfiguration,
  updateDataModelConfiguration,
  deleteDataModelConfiguration,
  assignClientToDataModelConfiguration,
  searchDocumentViews,
  exportDocumentViewsToExcel,
  getAggregatedDocumentViewsByContact,
  getAggregatedFundraisingDocumentViewsByContact,
  getAggregatedDocumentViewsByInvestor,
  getAggregatedFundraisingDocumentViewsByInvestor,
  getMessageAttachmentInfo,
  getFundraisingDocumentInfo,
  getFundraisings,
  getFundraisingById,
  getFundraisingAccess,
  getFundraisingAccessByFundIdAsExcelFile,
  createFundraising,
  updateFundraising,
  updateFundraisingAccess,
  deleteFundraising,
  uploadFundraisingFile,
  getFundraisingFileDownloadInfo,
  getDownloadUrlForMultipleFundraisingFiles,
  getDownloadUrlForMultipleFilesFromFundraisings,
  sendTestFundraisingEmail,
  getTestEmailContactsForFundraising,
  getFundraisingJobProgress,
  createFundraisingCategory,
  updateFundraisingCategory,
  deleteFundraisingCategory,
  getAllMetrics,
  getMetric,
  createMetric,
  updateMetric,
  deleteMetric,
  searchDataSubmissions,
  getAssignedDataSubmissions,
  getDataSubmissionDetails,
  saveSubmissionLayoutBlockInput,
  submitDataSubmission,
  exportSubmissionBlockInputToExcel,
  importSubmissionBlockInputFromExcel,
  searchObjectMetricExtensions,
  createObjectMetricExtensions,
  updateObjectMetricExtensions,
  renameObjectMetricExtension,
  searchDataCollectionRequests,
  getDataCollectionRequestDetails,
  createDataCollectionRequest,
  updateDataCollectionRequest,
  deleteDataCollectionRequest,
  publishDataCollectionRequest,
  cancelScheduledPublishOfDataRequest,
  addSubmissionsToDataRequest,
  publishDataRequestSubmissions,
  unpublishDataRequestSubmissions,
  requestChangesForDataRequestSubmissions,
  deleteDataRequestSubmissions,
  sendSubmissionReminders,
  updateDataRequestSubmissions,
  getDataRequestTemplates,
  getLatestRequestTemplateVersion,
  createDataRequestTemplateVersion,
  getDataSubmissionConfigurations,
  getExpenseManagementInboxAddresses,
  getInvoiceDocumentsDownload,
  getInvoiceApproversAssignment,
  getInvoiceActionChecks,
  executeInvoiceAction,
  previewInvoiceCard,
  getInvoiceSource,
  getInvoiceAgent,
  ensureBusinessCentralAccessIsEnabled,
  getBusinessCentralAccessJobStatus,
  resolveFundOpsTransactionLink,
  getVideoLibraryCategories,
  getVideosFromLibrary,
  getVideoItemFromLibrary,
  addVideoToLibrary,
  updateVideoInLibrary,
  deleteVideoFromLibrary,
  getUploadUrlForVideo,
  completeVideoUpload,
  searchInboxes,
  getInbox,
  createInbox,
  updateInbox,
  deleteInbox,
  getAgents,
  getDashboards,
  getDashboard,
  addDashboardDraft,
  updateDashboard,
  deleteDashboards,
  duplicateDashboard,
  copyDashboard,
  getActivityLogExportOptions,
  startActivityLogExport,
  getActivityLogExportJobProgress,
  startOrganizationContactsActivityLogExport,
  getOrganizationContactsActivityLogExportJobProgress,
  searchActivityLogs,
  getMcpApplicationState,
  setMcpApplicationState,
  getMcpPiiProfile,
  setMcpPiiProfile,
  searchInvestorCommunications,
  getInvestorCommunicationById,
  getInvestorCommunicationRecipients,
  getInvestorCommunicationEngagement,
  getInvestorCommunicationHeroImageDownloadInfo,
  createInvestorCommunication,
  updateInvestorCommunication,
  uploadInvestorCommunicationHeroImage,
  uploadInvestorCommunicationBodyImage,
  deleteInvestorCommunicationHeroImage,
  getInvestorCommunicationVideoUploadUrl,
  completeInvestorCommunicationVideoUpload,
  uploadInvestorCommunicationVideoThumbnail,
  deleteInvestorCommunicationVideo,
  deleteInvestorCommunicationVideoThumbnail,
  changeInvestorCommunicationStatus,
  deleteInvestorCommunication,
  getResourceChangesSubscriptionToken,
};

export const resourceChangesHubUrl = `${baseUri}/resourceChangesHub`;
export const assistantHubUrl = `${baseUri}/assistantHub`;

export default adminApi;
